import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Car,
  Search,
  MapPin,
  Users,
  Fuel,
  Calendar,
  Phone,
  X,
  CheckCircle,
  Building2,
  ShoppingCart,
  Clock,
  Gauge,
  Map,
  Navigation,
  ChevronRight,
  RefreshCw,
  ChevronLeft,
  ArrowUpDown,
  Locate,
  Loader2,
  UserPlus,
  LogIn,
  Cog as Settings,
  CheckCircle2,
  List,
  SlidersHorizontal,
  Mail,
  ArrowRightLeft,
  LayoutDashboard,
  AlertTriangle,
  ChevronDown,
  ShieldCheck,
  Tag,
  BadgeCheck,
  IndianRupee,
  Sparkles
} from 'lucide-react';
import { formatCurrency, formatVehicleName, cldImg } from '../../lib/utils';
import { ownerLabel } from '../../lib/vehiclePurpose';
import CarLoader from '../../components/ui/CarLoader';
import PriceRangeSlider from '../../components/ui/PriceRangeSlider';
import RadiusSlider from '../../components/ui/RadiusSlider';
import appConfig from '../../config/appConfig';
import NearbyCarsMap from '../../components/maps/NearbyCarsMap';
import AddressAutocomplete from '../../components/maps/AddressAutocomplete';
import CityAutocompleteInline from '../../components/maps/CityAutocompleteInline';
import { getCityFromCoordinates } from '../../utils/locationUtils';
import { useAuth } from '../../context/AuthContext';
import { useVehicleTypes } from '../../hooks/useVehicleTypes';
import SEO from '../../components/SEO';
import ListingDisclaimer from '../../components/legal/ListingDisclaimer';
import { onewayTripAPI } from '../../services/api';
import { ToastModal } from '../../components/ui/Toast';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// Indian registration plates. Yellow/black carry commercial paperwork a buyer
// inherits — permit, fitness certificate and commercial-rate insurance.
const BOARD_LABELS = {
  white: 'White — Private',
  yellow: 'Yellow — Commercial',
  green: 'Green — Electric (EV)',
  black: 'Black — Self-drive Rental',
};
const COMMERCIAL_BOARDS = ['yellow', 'black'];
const isCommercialBoard = (b) => COMMERCIAL_BOARDS.includes(b);

const PERMIT_LABELS = {
  state: 'State',
  national: 'National',
  'all-india': 'All India',
  'contract-carriage': 'Contract Carriage',
};

// Price means something different per tab: a rental is ₹/day, a sale is a lump
// sum. One bucket list can't serve both — ₹5,00,000 isn't "₹5,000+".
// `max: Infinity` closes the last bucket; `min` is exclusive so ranges don't overlap.
// `short` is used for the mobile chips, where the full label would wrap.
const RENTAL_PRICE_RANGES = [
  { value: 'all', label: 'Any Price', short: 'Any' },
  { value: '0-500', label: 'Under ₹500', short: 'Under ₹500', min: -1, max: 500 },
  { value: '500-1000', label: '₹500 - ₹1,000', short: '₹500-1K', min: 500, max: 1000 },
  { value: '1000-2000', label: '₹1,000 - ₹2,000', short: '₹1K-2K', min: 1000, max: 2000 },
  { value: '2000-5000', label: '₹2,000 - ₹5,000', short: '₹2K-5K', min: 2000, max: 5000 },
  { value: '5000+', label: '₹5,000+', short: '₹5K+', min: 5000, max: Infinity },
];

const SALE_PRICE_RANGES = [
  { value: 'all', label: 'Any Price', short: 'Any' },
  { value: 'sale-0-200000', label: 'Under ₹2L', short: 'Under ₹2L', min: -1, max: 200000 },
  { value: 'sale-200000-500000', label: '₹2L - ₹5L', short: '₹2L-5L', min: 200000, max: 500000 },
  { value: 'sale-500000-1000000', label: '₹5L - ₹10L', short: '₹5L-10L', min: 500000, max: 1000000 },
  { value: 'sale-1000000-2000000', label: '₹10L - ₹20L', short: '₹10L-20L', min: 1000000, max: 2000000 },
  { value: 'sale-2000000+', label: '₹20L+', short: '₹20L+', min: 2000000, max: Infinity },
];

const priceRangesFor = (tab) => (tab === 'sale' ? SALE_PRICE_RANGES : RENTAL_PRICE_RANGES);

// Slider bounds per tab. `max` doubles as the "and above" ceiling. Sale is a lump
// sum shown in lakhs; rental is ₹/day. Step keeps the drag feeling smooth.
const PRICE_BOUNDS = {
  sale: { min: 0, max: 5000000, step: 50000 },   // ₹0 – ₹50L
  rental: { min: 0, max: 10000, step: 100 },       // ₹0 – ₹10,000/day
};
const priceBoundsFor = (tab) => (tab === 'sale' ? PRICE_BOUNDS.sale : PRICE_BOUNDS.rental);

// Compact rupee label for the slider handles: sale in lakhs (₹2L, ₹12.5L),
// everything else in ₹K, small rental values as-is (₹500).
const fmtPrice = (v, tab) => {
  if (tab === 'sale') {
    if (v >= 100000) { const l = v / 100000; return `₹${Number.isInteger(l) ? l : l.toFixed(1)}L`; }
    return `₹${Math.round(v / 1000)}K`;
  }
  return v >= 1000 ? `₹${v % 1000 ? (v / 1000).toFixed(1) : v / 1000}K` : `₹${v}`;
};

// Used-car buyers judge age by the RTO registration year, not the build year.
// Where the seller gave one, it wins — it's the number on the RC.
const displayYear = (car) => (car.forSale && car.saleDetails?.registrationYear) || car.year;
// A for-sale car has pricePerDay 0, so sorting/filtering it by ₹/day is meaningless.
const priceOf = (car, tab) => (tab === 'sale' ? (car.salePrice || 0) : (car.pricePerDay || 0));

// An active loan blocks RC transfer, so it reads as a warning, not a fact.
const LOAN_STATUS = {
  none: { text: 'No loan taken', cls: 'text-green-600' },
  closed: { text: 'Closed — NOC available', cls: 'text-green-600' },
  active: { text: 'Running — NOC pending', cls: 'text-red-600' },
};

// ── Buy-tab filters ────────────────────────────────────────────────────────
// Each option carries its own predicate, so adding a filter is one entry here
// and nothing else. `test` receives the car; missing data never matches a
// narrowing option — we don't guess on the seller's behalf.
const regYearOf = (car) => car.saleDetails?.registrationYear || car.year || 0;
const kmOf = (car) => car.saleDetails?.kmDriven;

const BUY_FILTERS = [
  {
    key: 'year', label: 'Registration Year', icon: Calendar,
    options: [
      { value: 'all', label: 'Any year', short: 'Any' },
      { value: '2020+', label: '2020 & newer', short: '2020+', test: (c) => regYearOf(c) >= 2020 },
      { value: '2015-2019', label: '2015 – 2019', short: '2015-19', test: (c) => regYearOf(c) >= 2015 && regYearOf(c) <= 2019 },
      { value: '2010-2014', label: '2010 – 2014', short: '2010-14', test: (c) => regYearOf(c) >= 2010 && regYearOf(c) <= 2014 },
      { value: 'pre-2010', label: 'Before 2010', short: 'Pre-2010', test: (c) => regYearOf(c) > 0 && regYearOf(c) < 2010 },
    ],
  },
  {
    key: 'km', label: 'KM Driven', icon: Gauge,
    options: [
      { value: 'all', label: 'Any distance', short: 'Any' },
      { value: '0-30000', label: 'Under 30,000 km', short: '<30k', test: (c) => kmOf(c) != null && kmOf(c) <= 30000 },
      { value: '30000-60000', label: '30,000 – 60,000 km', short: '30-60k', test: (c) => kmOf(c) > 30000 && kmOf(c) <= 60000 },
      { value: '60000-100000', label: '60,000 – 1,00,000 km', short: '60k-1L', test: (c) => kmOf(c) > 60000 && kmOf(c) <= 100000 },
      { value: '100000+', label: 'Over 1,00,000 km', short: '1L+', test: (c) => kmOf(c) > 100000 },
    ],
  },
  {
    key: 'owners', label: 'Ownership', icon: Users,
    options: [
      { value: 'all', label: 'Any owner', short: 'Any' },
      { value: '1', label: 'First owner', short: '1st', test: (c) => c.saleDetails?.noOfOwners === 1 },
      { value: '2', label: 'Up to second', short: '≤2nd', test: (c) => c.saleDetails?.noOfOwners <= 2 },
      { value: '3', label: 'Up to third', short: '≤3rd', test: (c) => c.saleDetails?.noOfOwners <= 3 },
    ],
  },
  {
    key: 'board', label: 'Plate Type', icon: Tag,
    options: [
      { value: 'all', label: 'Any plate', short: 'Any' },
      { value: 'private', label: 'Private (white)', short: 'Private', test: (c) => !isCommercialBoard(c.registrationBoard) },
      { value: 'commercial', label: 'Commercial (yellow / black)', short: 'Commercial', test: (c) => isCommercialBoard(c.registrationBoard) },
    ],
  },
  {
    key: 'loan', label: 'Loan Status', icon: BadgeCheck,
    options: [
      { value: 'all', label: 'Any', short: 'Any' },
      { value: 'ready', label: 'Transfer ready', short: 'Transfer ready', test: (c) => ['none', 'closed'].includes(c.saleDetails?.loanStatus) },
      { value: 'active', label: 'Loan running', short: 'Loan running', test: (c) => c.saleDetails?.loanStatus === 'active' },
    ],
  },
  {
    key: 'condition', label: 'Condition', icon: ShieldCheck,
    options: [
      { value: 'all', label: 'Any condition', short: 'Any' },
      { value: 'excellent', label: 'Excellent', short: 'Excellent', test: (c) => c.saleDetails?.condition === 'excellent' },
      { value: 'good', label: 'Good or better', short: 'Good+', test: (c) => ['excellent', 'good'].includes(c.saleDetails?.condition) },
      { value: 'accident-free', label: 'Accident-free only', short: 'Accident-free', test: (c) => c.saleDetails?.accidentFree !== false },
    ],
  },
];

const BUY_DEFAULTS = Object.fromEntries(BUY_FILTERS.map((g) => [g.key, 'all']));

const matchesBuyFilters = (car, selected) => BUY_FILTERS.every((group) => {
  const value = selected[group.key];
  if (!value || value === 'all') return true;
  const option = group.options.find((o) => o.value === value);
  return option?.test ? option.test(car) : true;
});

// One collapsible group. Starts open on desktop (space to spare) and closed on
// mobile once a sheet has six of them, so the list stays thumb-reachable.
function BuyFilterGroup({ group, value, onChange, defaultOpen, counts = {} }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = group.icon;
  const active = value && value !== 'all';
  const activeLabel = group.options.find((o) => o.value === value)?.short;
  // No car in this fleet can answer this question — don't offer the question.
  const anyMatch = group.options.some((o) => o.test && counts[`${group.key}:${o.value}`] > 0);
  if (!anyMatch && !active) return null;

  return (
    <div className="border-b border-gray-100 last:border-0 pb-3 last:pb-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between py-2 group"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          <Icon className="h-4 w-4 text-blue-600" />
          {group.label}
          {active && (
            <span className="ml-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold">
              {activeLabel}
            </span>
          )}
        </span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {group.options.map((o) => {
            const n = counts[`${group.key}:${o.value}`];
            const dead = o.test && n === 0 && value !== o.value;
            return (
              <button
                key={o.value}
                onClick={() => !dead && onChange(group.key, o.value)}
                disabled={dead}
                aria-pressed={value === o.value}
                title={dead ? 'No vehicles match this' : undefined}
                className={`px-3 py-1.5 text-xs font-medium rounded-xl transition-colors ${
                  value === o.value
                    ? 'bg-blue-600 text-white shadow-sm'
                    : dead
                      ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {o.short}
                {o.test && n > 0 && value !== o.value && (
                  <span className="ml-1 text-[10px] text-gray-400">{n}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const fmtMonthYear = (d) => new Date(d).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' });

// A date a buyer relies on: red once lapsed, amber inside two months.
const expiryTone = (d) => {
  const days = Math.ceil((new Date(d) - new Date()) / 86400000);
  if (days < 0) return { cls: 'text-red-600', note: 'Expired' };
  if (days <= 60) return { cls: 'text-amber-600', note: 'Expiring soon' };
  return { cls: 'text-gray-900', note: '' };
};

// Service type tabs configuration
const SERVICE_TABS = [
  { id: 'all', label: 'All Vehicles', icon: Car, color: 'blue' },
  { id: 'rental', label: 'Multi-Day Rental', icon: Calendar, color: 'green' },
  { id: 'daily', label: 'Daily Rental', icon: Clock, color: 'purple' },
  { id: 'km-based', label: 'Pay Per KM', icon: Gauge, color: 'orange' },
  { id: 'outstation', label: 'Outstation', icon: Map, color: 'teal' },
  { id: 'local', label: 'Local Trips', icon: Navigation, color: 'indigo' },
  { id: 'sale', label: 'Buy Vehicle', icon: ShoppingCart, color: 'red' },
  { id: 'oneway', label: 'One-Way', icon: ArrowRightLeft, color: 'cyan' }
];

// Which tabs belong to each service group on Home. Used to filter the tab bar
// when arriving here via ?service=<group> so users only see related options.
const SERVICE_GROUPS = {
  rental: ['daily', 'rental', 'km-based', 'outstation', 'local'],
  oneway: ['oneway'],
  sale: ['sale'],
  packages: ['all']
};

// Vehicle type options with icons
const VEHICLE_TYPES = [
  { value: 'all', label: 'All Types', icon: '🚗' },
  { value: 'car', label: 'Car', icon: '🚗' },
  { value: 'suv', label: 'SUV / MUV', icon: '🚙' },
  { value: 'bike', label: 'Bike', icon: '🏍️' },
  { value: 'scooter', label: 'Scooter', icon: '🛵' },
  { value: 'auto', label: 'Auto', icon: '🛺' },
  { value: 'bus', label: 'Bus', icon: '🚌' },
  { value: 'tempo-traveller', label: 'Tempo', icon: '🚐' },
  { value: 'truck', label: 'Truck', icon: '🚛' },
  { value: 'van', label: 'Van', icon: '🚐' },
  { value: 'pickup', label: 'Pickup', icon: '🛻' },
  { value: 'tractor', label: 'Tractor', icon: '🚜' },
  { value: 'jcb', label: 'JCB', icon: 'jcb_img' },
  { value: 'ambulance', label: 'Ambulance', icon: '🚑' },
  { value: 'other', label: 'Other', icon: '🚘' }
];

// Vehicle-specific radius options (halved distances)
const VEHICLE_RADIUS_CONFIG = {
  bike:    { buttons: [1, 2, 3, 4, 5],          dropdown: [6, 7, 8, 10],                  defaultRadius: 2 },
  scooter: { buttons: [1, 2, 3, 4, 5],          dropdown: [6, 7, 8, 10],                  defaultRadius: 2 },
  auto:    { buttons: [1, 2, 3, 4, 5],          dropdown: [6, 7, 8],                      defaultRadius: 2 },
  car:     { buttons: [1, 2, 3, 4, 5],          dropdown: [8, 10, 13, 15, 20, 25, 38, 50], defaultRadius: 3 },
  suv:     { buttons: [1, 2, 3, 4, 5],          dropdown: [8, 10, 13, 15, 20, 25, 38, 50], defaultRadius: 3 },
  van:     { buttons: [1, 2, 3, 4, 5],          dropdown: [8, 10, 13, 15, 25, 38, 50],    defaultRadius: 3 },
  pickup:  { buttons: [1, 2, 3, 4, 5],          dropdown: [8, 10, 13, 15, 25, 38, 50],    defaultRadius: 3 },
  bus:     { buttons: [3, 5, 8, 10, 15],        dropdown: [20, 25, 38, 50, 75, 100],      defaultRadius: 5 },
  'tempo-traveller': { buttons: [3, 5, 8, 10, 15], dropdown: [20, 25, 38, 50, 75, 100],  defaultRadius: 5 },
  truck:   { buttons: [3, 5, 8, 10, 15],        dropdown: [20, 25, 38, 50, 75, 100, 150], defaultRadius: 5 },
  tractor: { buttons: [1, 2, 3, 4, 5],          dropdown: [6, 7, 8, 10],                  defaultRadius: 2 },
  jcb:     { buttons: [1, 2, 3, 4, 5],          dropdown: [6, 7, 8, 10],                  defaultRadius: 2 },
  ambulance: { buttons: [1, 2, 3, 4, 5],        dropdown: [8, 10, 13, 15, 25, 38, 50],    defaultRadius: 3 },
  all:     { buttons: [1, 2, 3, 4, 5],          dropdown: [8, 10, 15, 20, 25],            defaultRadius: 3 },
  other:   { buttons: [1, 2, 3, 4, 5],          dropdown: [8, 10, 13, 15, 20, 25, 38, 50], defaultRadius: 3 },
};

const getRadiusConfig = (vehicleType) => VEHICLE_RADIUS_CONFIG[vehicleType] || VEHICLE_RADIUS_CONFIG.all;

// Capitalize first letter of each word
const titleCase = (str) => {
  if (!str) return '';
  return str.replace(/\b\w/g, c => c.toUpperCase());
};

// Emoji fallbacks for image-based icons — <option> can't render an <img>,
// so PNG-backed types (e.g. JCB) get a glyph here for dropdown menus.
const OPTION_ICON_FALLBACK = { jcb_img: '🏗️' };
const optionGlyph = (icon) => {
  if (!icon) return '';
  if (icon.includes('_')) return OPTION_ICON_FALLBACK[icon] || '';
  return icon;
};

// Helper to render vehicle icon - handles JCB image
const VehicleIcon = ({ type, size = 'sm' }) => {
  const icon = VEHICLE_TYPES.find(v => v.value === type)?.icon || '🚗';
  if (icon === 'jcb_img') {
    const sizeClass = size === 'lg' ? 'w-8 h-8' : size === 'md' ? 'w-5 h-5' : 'w-4 h-4';
    return <img src="/images/icons/jcb.png" alt="JCB" className={`${sizeClass} object-contain inline`} />;
  }
  return <>{icon}</>;
};

const BrowseCars = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: authUser, loginWithOtp, logout: authLogout } = useAuth();
  // Keep the existing filter options, and append any new admin-added vehicle types
  // so they show up as filters without disturbing the current labels/icons.
  const { vehicleTypes: liveVehicleTypes } = useVehicleTypes();
  const vehicleTypeOptions = React.useMemo(() => {
    const existing = new Set(VEHICLE_TYPES.map(v => v.value));
    const extras = liveVehicleTypes
      .filter(t => !existing.has(t.value))
      .map(t => ({ value: t.value, label: t.label, icon: t.icon }));
    return [...VEHICLE_TYPES, ...extras];
  }, [liveVehicleTypes]);
  const [cars, setCars] = useState([]);
  const [allCars, setAllCars] = useState([]); // Store all cars for location extraction
  const [onewayTrips, setOnewayTrips] = useState([]);
  const [publicPackages, setPublicPackages] = useState([]);
  const [onewayFrom, setOnewayFrom] = useState('');
  const [onewayTo, setOnewayTo] = useState('');
  const [onewayDate, setOnewayDate] = useState('');
  const [onewaySearched, setOnewaySearched] = useState(false);
  // One-way book modal state
  const [onewayBookModal, setOnewayBookModal] = useState(false);
  const [bookingTrip, setBookingTrip] = useState(null);
  const [bookSeats, setBookSeats] = useState(1);
  const [bookName, setBookName] = useState('');
  const [bookPhone, setBookPhone] = useState('');
  const [bookEmail, setBookEmail] = useState('');
  const [bookSubmitting, setBookSubmitting] = useState(false);
  // After a successful booking, owner contact is revealed in this object
  const [bookSuccess, setBookSuccess] = useState(null);
  // App-style toast for one-way feedback
  const [bookToast, setBookToast] = useState({ open: false, title: '', message: '', type: 'success' });
  const [outstationFrom, setOutstationFrom] = useState('');
  const [outstationTo, setOutstationTo] = useState('');
  const [outstationDate, setOutstationDate] = useState('');
  const [outstationSearched, setOutstationSearched] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedVehicleType, setSelectedVehicleType] = useState('all');
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'all');
  // Optional service group from URL (?service=rental|oneway|sale|packages) —
  // when present, the tab bar only shows tabs from that group.
  const serviceGroup = searchParams.get('service');
  const visibleTabs = serviceGroup && SERVICE_GROUPS[serviceGroup]
    ? SERVICE_TABS.filter(t => SERVICE_GROUPS[serviceGroup].includes(t.id))
    : SERVICE_TABS;
  // When the user clicks "Clear filters" we should return them to the default
  // tab of their current service group — not jump them back to "All Vehicles".
  // e.g. on One-Way, clearing should stay on 'oneway'.
  const defaultTab = serviceGroup && SERVICE_GROUPS[serviceGroup]
    ? SERVICE_GROUPS[serviceGroup][0]
    : 'all';

  // Fetch all public tour packages when the Packages service tab is active
  useEffect(() => {
    if (serviceGroup !== 'packages') return;
    fetch(`${API_BASE}/packages/all-public`)
      .then(r => r.json())
      .then(d => { if (d?.success) setPublicPackages(d.packages || []); })
      .catch(() => setPublicPackages([]));
  }, [serviceGroup]);

  const [selectedCar, setSelectedCar] = useState(null);
  const [bookingModal, setBookingModal] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [bookingData, setBookingData] = useState({
    phone: '',
    name: '',
    startDate: '',
    endDate: '',
    pickupLocation: '',
    dropoffLocation: '',
    estimatedKm: '',
    hours: '',
    notes: '',
    rentalType: 'daily'
  });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingStep, setBookingStep] = useState('form'); // 'form' | 'confirm'
  const [bookingResult, setBookingResult] = useState(null);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const [sortBy, setSortBy] = useState('default');
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [detailsModal, setDetailsModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'map'
  const [detectedCity, setDetectedCity] = useState('');

  // Track which service group is being hovered on desktop, to show sub-tabs dropdown
  const [hoveredGroup, setHoveredGroup] = useState(null);

  // Auto-hide top search bar on scroll down, show on scroll up
  const mainScrollRef = useRef(null);
  // Location bar stays visible always — no scroll-based hide/show
  const [hideTopBar] = useState(false);

  // Normalize Indian city names so common alternates (Bengaluru/Bangalore,
  // Mumbai/Bombay, etc.) match each other when sorting by location.
  const cityKey = (s) => {
    if (!s) return '';
    const first = s.toLowerCase().trim().split(/[,\s]+/)[0];
    const aliases = {
      bengaluru: 'bangalore',
      bombay: 'mumbai',
      calcutta: 'kolkata',
      madras: 'chennai',
      gurgaon: 'gurugram',
      vadodara: 'baroda',
      mysuru: 'mysore',
      thiruvananthapuram: 'trivandrum'
    };
    return aliases[first] || first;
  };

  // Sort packages — ones starting from the user's detected city come first
  const sortedPackages = React.useMemo(() => {
    if (!publicPackages || publicPackages.length === 0) return [];
    const myCityKey = cityKey(detectedCity);
    if (!myCityKey) return publicPackages;
    return [...publicPackages].sort((a, b) => {
      const aMatch = cityKey(a.startPoint) === myCityKey;
      const bMatch = cityKey(b.startPoint) === myCityKey;
      if (aMatch && !bMatch) return -1;
      if (!aMatch && bMatch) return 1;
      return 0;
    });
  }, [publicPackages, detectedCity]);
  const isFromCity = (pkg) =>
    !!detectedCity && cityKey(pkg.startPoint) === cityKey(detectedCity);

  // Package search — input state (what user is typing) vs applied state
  // (what's actually filtering the list). Filters only apply when the user
  // clicks "Search".
  const [pkgFrom, setPkgFrom] = useState('');
  const [pkgTo, setPkgTo] = useState('');
  const [pkgDate, setPkgDate] = useState('');
  const [pkgDuration, setPkgDuration] = useState('all'); // 'all' | '1' | '2-3' | '4+'
  const [pkgPriceMax, setPkgPriceMax] = useState('all'); // 'all' | '5000' | '10000' | '20000'

  // Applied filters — what actually drives filteredPackages
  const [appliedPkg, setAppliedPkg] = useState({
    from: '', to: '', date: '', duration: 'all', priceMax: 'all'
  });

  // Pre-fill From with detected city the first time it resolves (only if user hasn't typed anything yet)
  const [pkgFromAutoFilled, setPkgFromAutoFilled] = React.useState(false);
  useEffect(() => {
    if (!pkgFromAutoFilled && detectedCity && !pkgFrom) {
      setPkgFrom(detectedCity);
      setPkgFromAutoFilled(true);
    }
  }, [detectedCity, pkgFrom, pkgFromAutoFilled]);

  // Unique cities found in packages — for datalist suggestions
  const packageCities = React.useMemo(() => {
    const set = new Set();
    publicPackages.forEach(p => {
      if (p.startPoint) set.add(p.startPoint);
      (p.places || []).forEach(pl => pl && set.add(pl));
    });
    return Array.from(set).sort();
  }, [publicPackages]);

  // Apply filters on top of the location-sorted list — uses APPLIED state, not live input
  const filteredPackages = React.useMemo(() => {
    let list = sortedPackages;
    if (appliedPkg.from.trim()) {
      const key = cityKey(appliedPkg.from);
      const raw = appliedPkg.from.toLowerCase().trim();
      list = list.filter(p => cityKey(p.startPoint) === key || (p.startPoint || '').toLowerCase().includes(raw));
    }
    if (appliedPkg.to.trim()) {
      const key = cityKey(appliedPkg.to);
      const raw = appliedPkg.to.toLowerCase().trim();
      list = list.filter(p => (p.places || []).some(pl => cityKey(pl) === key || pl.toLowerCase().includes(raw)));
    }
    if (appliedPkg.duration !== 'all') {
      list = list.filter(p => {
        const d = Number(p.duration?.value) || 0;
        if (appliedPkg.duration === '1') return d === 1;
        if (appliedPkg.duration === '2-3') return d >= 2 && d <= 3;
        if (appliedPkg.duration === '4+') return d >= 4;
        return true;
      });
    }
    if (appliedPkg.priceMax !== 'all') {
      const max = Number(appliedPkg.priceMax);
      list = list.filter(p => {
        const final = (p.basePrice || 0) - ((p.basePrice || 0) * (p.discountPercentage || 0) / 100);
        return final <= max;
      });
    }
    return list;
  }, [sortedPackages, appliedPkg]);

  const pkgFiltersActive = !!(appliedPkg.from || appliedPkg.to || appliedPkg.date || appliedPkg.duration !== 'all' || appliedPkg.priceMax !== 'all');
  // True when current input differs from what's applied — used to highlight Search button
  const pkgHasUnappliedChanges =
    pkgFrom !== appliedPkg.from ||
    pkgTo !== appliedPkg.to ||
    pkgDate !== appliedPkg.date ||
    pkgDuration !== appliedPkg.duration ||
    pkgPriceMax !== appliedPkg.priceMax;

  const applyPkgFilters = () => setAppliedPkg({ from: pkgFrom, to: pkgTo, date: pkgDate, duration: pkgDuration, priceMax: pkgPriceMax });
  const clearPkgFilters = () => {
    setPkgFrom(''); setPkgTo(''); setPkgDate(''); setPkgDuration('all'); setPkgPriceMax('all');
    setAppliedPkg({ from: '', to: '', date: '', duration: 'all', priceMax: 'all' });
  };
  const [searchRadius, setSearchRadius] = useState(3); // Distance in km for nearby search (default 3km)
  const [sidebarOpen, setSidebarOpen] = useState(false); // Mobile sidebar toggle
  const [showLocationSearch, setShowLocationSearch] = useState(false); // Manual location search
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [filterFuelType, setFilterFuelType] = useState('all');
  const [filterTransmission, setFilterTransmission] = useState('all');
  const [filterSeats, setFilterSeats] = useState('all');
  // Price is a dual-handle range [lo, hi] in rupees; null = "any". Bounds differ
  // per tab (₹/day vs sale lakhs), so a range set on one tab must not survive a
  // switch to the other — the tab-change effect resets it to null.
  const [priceRange, setPriceRange] = useState(null);
  const [filterFeatures, setFilterFeatures] = useState([]);

  const priceBounds = priceBoundsFor(activeTab);
  const priceActive = !!priceRange && (priceRange[0] > priceBounds.min || priceRange[1] < priceBounds.max);
  const priceLabel = priceRange ? `${fmtPrice(priceRange[0], activeTab)} – ${fmtPrice(priceRange[1], activeTab)}${priceRange[1] >= priceBounds.max ? '+' : ''}` : '';
  const [buyFilters, setBuyFilters] = useState(BUY_DEFAULTS);
  const isBuyTab = activeTab === 'sale';
  const buyActiveCount = Object.values(buyFilters).filter((v) => v !== 'all').length;
  const setBuyFilter = (key, value) => setBuyFilters((f) => ({ ...f, [key]: value }));
  const clearBuyFilters = () => setBuyFilters(BUY_DEFAULTS);

  // Top-level intent split: Buy = the sale tab, everything else is renting. The
  // rental sub-types (Rental/One-Way/Packages) live under "Rent".
  const buyMode = activeTab === 'sale' || serviceGroup === 'sale';
  const goBuy = () => { setActiveTab('sale'); setSearchParams({ service: 'sale', tab: 'sale' }); };
  const goRent = () => { setActiveTab('all'); setSearchParams({}); };

  // Shared filter styling for a consistent, polished look across all pill groups.
  const pillCls = (active) =>
    `px-2.5 py-1 text-xs font-semibold rounded-full border transition-all ${
      active
        ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
        : 'bg-white text-gray-600 border-gray-200 hover:border-blue-400 hover:text-blue-600'
    }`;
  const sectionLabelCls = 'flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-2';

  useEffect(() => {
    setPriceRange(null);
    setBuyFilters(BUY_DEFAULTS);
  }, [activeTab]);
  const [filterAvailability, setFilterAvailability] = useState('all'); // 'all' | 'available'

  // OTP verification states
  const [otpStep, setOtpStep] = useState(authUser ? 'verified' : 'phone'); // 'phone' | 'otp' | 'verified'
  const [otpMethod, setOtpMethod] = useState('email'); // 'phone' | 'email' — default to email since SMS not configured
  const [otpEmail, setOtpEmail] = useState(''); // Email for email OTP
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [verifiedCustomer, setVerifiedCustomer] = useState(authUser ? { phone: authUser.phone, name: authUser.name, isNew: false } : null);

  // Lock body scroll when mobile filter is open
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  // Auto-detect location on mount, then fetch cars
  const [locationDone, setLocationDone] = useState(false);

  useEffect(() => {
    detectLocation();
  }, []);

  useEffect(() => {
    fetchCars();
  }, [activeTab, locationDone]);

  // Fallback: get location from Google Maps Geolocation API (accurate on laptops)
  const detectLocationFromGoogle = async () => {
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) return false;

      const response = await fetch(
        `https://www.googleapis.com/geolocation/v1/geolocate?key=${apiKey}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ considerIp: true }) }
      );
      const data = await response.json();
      if (data.location?.lat && data.location?.lng) {
        const location = { latitude: data.location.lat, longitude: data.location.lng };
        setUserLocation(location);

        // Get city name from coordinates
        try {
          const cityData = await getCityFromCoordinates(location.latitude, location.longitude);
          setDetectedCity(titleCase(cityData.city || ''));
        } catch (err) {
          console.error('Error getting city:', err);
        }

        setLocationError('');
        setLocationLoading(false);
        setLocationDone(true);
        setSortBy('nearest');
        return true;
      }
    } catch (err) {
      console.error('Google geolocation failed:', err);
    }
    return false;
  };

  // Detect user location using Google Geolocation API (primary) with browser fallback
  const detectLocation = async () => {
    setLocationLoading(true);
    setLocationError('');

    // Try Google Geolocation API first — most accurate on laptops
    const googleSuccess = await detectLocationFromGoogle();
    if (googleSuccess) return;

    // Fallback to browser geolocation
    if (!navigator.geolocation) {
      setLocationLoading(false);
      setLocationDone(true);
      setShowLocationSearch(true); // Show search bar so user can enter location
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
        setUserLocation(location);

        try {
          const cityData = await getCityFromCoordinates(location.latitude, location.longitude);
          setDetectedCity(titleCase(cityData.city || ''));
        } catch (err) {
          console.error('Error getting city:', err);
        }

        setLocationLoading(false);
        setLocationDone(true);
        setSortBy('nearest');
      },
      (error) => {
        console.error('Geolocation error:', error);
        setLocationLoading(false);
        setLocationDone(true);
        setShowLocationSearch(true); // Show search bar so user can enter location
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 0 }
    );
  };

  const fetchCars = async () => {
    // Wait for location detection to finish before showing cars
    if (!locationDone) return;

    try {
      setLoading(true);

      // One-way trips are fetched via searchOnewayTrips, not here
      if (activeTab === 'oneway') {
        setLoading(false);
        return;
      }

      // Outstation with search uses searchOutstationCabs instead
      if (activeTab === 'outstation' && outstationSearched) {
        setLoading(false);
        return;
      }

      let url = `${API_BASE}/cars/available`;

      if (activeTab !== 'all') {
        url += `?serviceType=${activeTab}`;
      }

      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        const carsData = data.cars || [];
        setCars(carsData);
        // Store all cars on initial load for location extraction
        if (activeTab === 'all' && allCars.length === 0) {
          setAllCars(carsData);
        }
      }
    } catch (error) {
      console.error('Error fetching cars:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchOnewayTrips = async () => {
    if (!onewayFrom.trim() || !onewayTo.trim()) return;
    setLoading(true);
    setOnewaySearched(true);
    try {
      const params = { fromCity: onewayFrom.trim(), toCity: onewayTo.trim() };
      if (onewayDate) params.date = onewayDate;
      const owResponse = await fetch(`${API_BASE}/oneway-trips?${new URLSearchParams(params)}`);
      const owData = await owResponse.json();
      if (owData.success) {
        setOnewayTrips(owData.data || []);
      }
    } catch (err) {
      setOnewayTrips([]);
    } finally {
      setLoading(false);
    }
  };

  // Open the One-Way booking modal — same auth rule as other booking flows:
  // any authenticated identity (authUser OR an OTP-verified customer) is fine.
  const handleBookOnewayTrip = (trip) => {
    const isLoggedIn = authUser || verifiedCustomer;
    if (!isLoggedIn) {
      setBookToast({
        open: true,
        title: 'Please log in',
        message: "You need to sign in before booking a one-way trip. We'll take you to the login page now.",
        type: 'info',
        onClose: () => { setBookToast(t => ({ ...t, open: false })); navigate('/customer-login'); }
      });
      return;
    }
    if ((trip.seatsAvailable || 0) <= 0) {
      setBookToast({ open: true, title: 'Sold Out', message: 'Sorry, no seats left on this trip.', type: 'error' });
      return;
    }
    setBookingTrip(trip);
    setBookSeats(1);
    setBookName(authUser?.name || verifiedCustomer?.name || '');
    setBookPhone(authUser?.phone || verifiedCustomer?.phone || '');
    setBookEmail(authUser?.email || '');
    setBookSuccess(null);
    setOnewayBookModal(true);
  };

  // Submit the booking from the modal
  const submitOnewayBook = async () => {
    if (!bookingTrip) return;
    const available = bookingTrip.seatsAvailable || 0;
    const seats = parseInt(bookSeats) || 0;
    const cleanPhone = (bookPhone || '').replace(/\D/g, '').slice(-10);
    if (!bookName.trim()) {
      setBookToast({ open: true, title: 'Name required', message: 'Please enter your name.', type: 'error' });
      return;
    }
    if (cleanPhone.length < 10) {
      setBookToast({ open: true, title: 'Phone required', message: 'Please enter a valid 10-digit phone number.', type: 'error' });
      return;
    }
    if (seats < 1 || seats > available) {
      setBookToast({ open: true, title: 'Invalid Seats', message: `Please choose between 1 and ${available} seat${available > 1 ? 's' : ''}.`, type: 'error' });
      return;
    }
    setBookSubmitting(true);
    try {
      const res = await onewayTripAPI.book(bookingTrip._id, {
        seats,
        customerName: bookName.trim(),
        customerPhone: cleanPhone,
        customerEmail: bookEmail.trim()
      });
      const owner = res?.data?.data?.owner || {};
      // Switch to success view; reveal owner contact only after booking
      setBookSuccess({ owner, seats, total: (bookingTrip.price || 0) * seats });
      searchOnewayTrips();
    } catch (err) {
      setBookToast({
        open: true,
        title: 'Could not book',
        message: err.response?.data?.message || 'Please try again in a moment.',
        type: 'error'
      });
    } finally {
      setBookSubmitting(false);
    }
  };

  const searchOutstationCabs = async () => {
    if (!outstationFrom.trim() || !outstationTo.trim()) return;
    setLoading(true);
    setOutstationSearched(true);
    try {
      let url = `${API_BASE}/cars/available?serviceType=outstation`;
      if (outstationFrom.trim()) url += `&city=${outstationFrom.trim()}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setCars(data.cars || []);
      }
    } catch (err) {
      setCars([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate distance between two coordinates using Haversine formula
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;

    const R = 6371; // Earth's radius in kilometers
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c; // Distance in km
  };

  // Get user's current location
  const getUserLocation = () => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    setLocationLoading(true);
    setLocationError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setLocationLoading(false);
      },
      (error) => {
        setLocationLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Location access denied. Please enable location permissions.');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Location information unavailable.');
            break;
          case error.TIMEOUT:
            setLocationError('Location request timed out.');
            break;
          default:
            setLocationError('Unable to get your location.');
        }
        setSortBy('default');
      }
    );
  };

  // Handle sort change
  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    if (newSort === 'nearest' && !userLocation) {
      getUserLocation();
    }
  };

  // Get unique locations from all cars
  const uniqueLocations = [...new Set(
    (allCars.length > 0 ? allCars : cars)
      .map(car => car.location?.city)
      .filter(Boolean)
  )].sort();

  const filteredCars = cars.filter(car => {
    // Location filter
    const matchesLocation = selectedLocation === 'all' ||
      car.location?.city?.toLowerCase() === selectedLocation.toLowerCase();

    // Vehicle type filter
    const matchesVehicleType = selectedVehicleType === 'all' ||
      (car.vehicleType || 'car') === selectedVehicleType;

    // Search filter
    const carFullName = `${car.make || ''} ${car.model || ''}`.toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      car.make?.toLowerCase().includes(searchLower) ||
      car.model?.toLowerCase().includes(searchLower) ||
      carFullName.includes(searchLower) ||
      car.location?.city?.toLowerCase().includes(searchLower);

    // Fuel type filter
    const matchesFuel = filterFuelType === 'all' ||
      car.fuelType?.toLowerCase() === filterFuelType.toLowerCase();

    // Transmission filter
    const matchesTransmission = filterTransmission === 'all' ||
      car.transmission?.toLowerCase() === filterTransmission.toLowerCase();

    // Seating filter
    const matchesSeats = filterSeats === 'all' || (() => {
      const s = car.seats || 0;
      if (filterSeats === '2') return s <= 2;
      if (filterSeats === '4-5') return s >= 4 && s <= 5;
      if (filterSeats === '7+') return s >= 7;
      return true;
    })();

    // Price range filter — dual-handle slider [lo, hi]; hi at the ceiling means
    // "and above". Uses ₹/day on rental tabs, sale price on the Buy tab.
    const matchesPrice = !priceActive || (() => {
      const [lo, hi] = priceRange;
      const p = priceOf(car, activeTab);
      return p >= lo && (hi >= priceBounds.max || p <= hi);
    })();

    // Features filter
    const matchesFeatures = filterFeatures.length === 0 ||
      filterFeatures.every(f => (car.features || []).some(cf => cf.toLowerCase().includes(f.toLowerCase())));

    // Availability filter
    const matchesAvailability = filterAvailability === 'all' || car.isAvailable !== false;

    // Buy-only filters never narrow the rental tabs.
    const matchesBuy = !isBuyTab || matchesBuyFilters(car, buyFilters);

    return matchesLocation && matchesVehicleType && matchesSearch && matchesFuel && matchesTransmission && matchesSeats && matchesPrice && matchesFeatures && matchesAvailability && matchesBuy;
  });

  // How many for-sale vehicles each option would return. An option matching zero
  // cars is disabled rather than hidden — a buyer learns "no first-owner cars
  // here" instead of clicking into an empty page.
  const buyOptionCounts = useMemo(() => {
    if (!isBuyTab) return {};
    const saleCars = cars.filter((c) => c.forSale);
    const counts = {};
    BUY_FILTERS.forEach((group) => {
      // Count against the other groups' selections, so counts narrow as you pick.
      const others = { ...buyFilters, [group.key]: 'all' };
      const pool = saleCars.filter((c) => matchesBuyFilters(c, others));
      group.options.forEach((o) => {
        counts[`${group.key}:${o.value}`] = o.test ? pool.filter(o.test).length : pool.length;
      });
    });
    return counts;
  }, [cars, buyFilters, isBuyTab]);

  // Add distance to each car and sort
  const carsWithDistance = filteredCars.map(car => {
    if (userLocation && car.location?.coordinates?.latitude) {
      const distance = calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        car.location.coordinates.latitude,
        car.location.coordinates.longitude
      );
      return { ...car, distance: Math.round(distance * 10) / 10 };
    }
    return { ...car, distance: null };
  });

  // Sort cars based on sortBy option
  const sortedCars = [...carsWithDistance].sort((a, b) => {
    switch (sortBy) {
      case 'nearest':
        if (a.distance === null) return 1;
        if (b.distance === null) return -1;
        return a.distance - b.distance;
      case 'price-low':
        return priceOf(a, activeTab) - priceOf(b, activeTab);
      case 'price-high':
        return priceOf(b, activeTab) - priceOf(a, activeTab);
      case 'newest':
        return (b.year || 0) - (a.year || 0);
      case 'name-asc':
        return `${a.make} ${a.model}`.localeCompare(`${b.make} ${b.model}`);
      case 'name-desc':
        return `${b.make} ${b.model}`.localeCompare(`${a.make} ${a.model}`);
      default:
        return 0;
    }
  });

  // Reset page when filters or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedLocation, selectedVehicleType, activeTab, sortBy, searchRadius]);

  // Reset radius to vehicle-specific default only if user hasn't manually changed it
  const [radiusManuallySet, setRadiusManuallySet] = useState(false);
  useEffect(() => {
    if (!radiusManuallySet) {
      const config = getRadiusConfig(selectedVehicleType);
      const defaultRadius = config.defaultRadius || config.buttons[2] || 3;
      setSearchRadius(defaultRadius);
    }
  }, [selectedVehicleType]);

  // OTP countdown timer
  useEffect(() => {
    if (otpTimer <= 0) return;
    const interval = setInterval(() => {
      setOtpTimer(prev => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Send OTP (phone or email)
  const handleSendOtp = async () => {
    if (otpMethod === 'phone') {
      const phone = bookingData.phone.trim().replace(/\D/g, '').slice(-10);
      if (phone.length < 10) {
        setOtpError('Please enter a valid 10-digit phone number');
        return;
      }
    } else {
      if (!otpEmail || !otpEmail.includes('@')) {
        setOtpError('Please enter a valid email address');
        return;
      }
    }

    setOtpLoading(true);
    setOtpError('');

    try {
      const endpoint = otpMethod === 'phone' ? '/users/send-otp' : '/users/send-email-otp';
      const body = otpMethod === 'phone'
        ? { phone: bookingData.phone.trim().replace(/\D/g, '').slice(-10) }
        : { email: otpEmail.trim() };

      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();

      if (data.success) {
        setOtpStep('otp');
        setOtpTimer(30);
        if (data.otp) {
        }
      } else {
        setOtpError(data.message || 'Failed to send OTP');
      }
    } catch (err) {
      setOtpError('Something went wrong. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    const otpCode = otp.join('');
    if (otpCode.length < 6) {
      setOtpError('Please enter the 6-digit OTP');
      return;
    }

    const body = otpMethod === 'phone'
      ? { phone: bookingData.phone.trim().replace(/\D/g, '').slice(-10), otp: otpCode }
      : { email: otpEmail.trim(), otp: otpCode };

    setOtpLoading(true);
    setOtpError('');

    try {
      const response = await fetch(`${API_BASE}/users/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();

      if (data.success) {
        // Persist session in AuthContext + localStorage
        loginWithOtp(data.token, data.user);

        const customerPhone = data.user?.phone || bookingData.phone.trim().replace(/\D/g, '').slice(-10);
        setVerifiedCustomer({
          phone: customerPhone,
          name: data.user?.name || '',
          isNew: data.isNew || false
        });
        // Pre-fill name and phone
        const userName = data.user?.name || '';
        const isAutoName = userName.startsWith('User ') || userName.startsWith('Customer ');
        setBookingData(prev => ({
          ...prev,
          phone: customerPhone,
          name: isAutoName ? '' : userName
        }));
        setOtpStep('verified');
          } else {
        setOtpError(data.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setOtpError('Something went wrong. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    setOtpLoading(true);
    setOtpError('');

    try {
      const endpoint = otpMethod === 'phone' ? '/users/resend-otp' : '/users/send-email-otp';
      const body = otpMethod === 'phone'
        ? { phone: bookingData.phone.trim().replace(/\D/g, '').slice(-10) }
        : { email: otpEmail.trim() };

      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();

      if (data.success) {
        setOtpTimer(30);
        setOtp(['', '', '', '', '', '']);
      } else {
        setOtpError(data.message || 'Failed to resend OTP');
      }
    } catch (err) {
      setOtpError('Something went wrong.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Handle OTP input change with auto-focus
  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...otp];
      digits.forEach((d, i) => { if (index + i < 6) newOtp[index + i] = d; });
      setOtp(newOtp);
      const nextIndex = Math.min(index + digits.length, 5);
      document.getElementById(`otp-input-${nextIndex}`)?.focus();
      return;
    }
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      document.getElementById(`otp-input-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-input-${index - 1}`)?.focus();
    }
  };

  // Filter cars by search radius if user location is available
  const radiusFilteredCars = userLocation
    ? sortedCars.filter(car => car.distance !== null && car.distance <= searchRadius)
    : sortedCars;

  // Pagination calculations
  const totalPages = Math.ceil(radiusFilteredCars.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCars = radiusFilteredCars.slice(startIndex, endIndex);

  // Handle refresh
  const handleRefresh = () => {
    fetchCars();
    setCurrentPage(1);
  };

  // Handle view details - show car details modal first
  const handleViewDetails = (car) => {
    setSelectedCar(car);
    setSelectedImageIndex(0);
    setDetailsModal(true);
    // Switch to list view when viewing details from map
    if (viewMode === 'map') {
      setViewMode('list');
    }
  };

  const handleBookNow = (car) => {
    setSelectedCar(car);
    setBookingModal(true);
    setError('');
    setBookingStep('form');

    // Skip OTP if already logged in (authUser) or verified in session
    const isLoggedIn = authUser || verifiedCustomer;
    if (!isLoggedIn) {
      setOtpStep('phone');
      setOtp(['', '', '', '', '', '']);
      setOtpError('');
      } else {
      setOtpStep('verified');
    }

    // Set default dates and rental type based on service
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    let rentalType = 'daily';
    if (activeTab !== 'all' && activeTab !== 'sale') {
      rentalType = activeTab;
    } else if (car.serviceTypes && car.serviceTypes.length > 0) {
      rentalType = car.serviceTypes[0];
    }

    const customerPhone = authUser?.phone || verifiedCustomer?.phone || '';
    const customerName = authUser?.name || verifiedCustomer?.name || '';
    const isAutoName = customerName.startsWith('User ') || customerName.startsWith('Customer ');

    setBookingData({
      phone: customerPhone,
      name: isAutoName ? '' : customerName,
      startDate: today.toISOString().split('T')[0],
      endDate: tomorrow.toISOString().split('T')[0],
      pickupLocation: car.location?.city || '',
      dropoffLocation: car.location?.city || '',
      estimatedKm: '',
      hours: '4',
      notes: '',
      rentalType
    });
  };

  const handleInquiry = (car) => {
    setSelectedCar(car);
    setBookingModal(true);
    setError('');
    setBookingStep('form');

    const isLoggedIn = authUser || verifiedCustomer;
    if (!isLoggedIn) {
      setOtpStep('phone');
      setOtp(['', '', '', '', '', '']);
      setOtpError('');
      } else {
      setOtpStep('verified');
    }

    const customerPhone = authUser?.phone || verifiedCustomer?.phone || '';
    const customerName = authUser?.name || verifiedCustomer?.name || '';
    const isAutoName = customerName.startsWith('User ') || customerName.startsWith('Customer ');

    setBookingData({
      phone: customerPhone,
      name: isAutoName ? '' : customerName,
      startDate: '',
      endDate: '',
      pickupLocation: '',
      dropoffLocation: '',
      estimatedKm: '',
      hours: '',
      notes: 'I am interested in buying this vehicle',
      rentalType: 'sale'
    });
  };

  // Validate and show confirmation step
  const handleBookingSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!bookingData.phone || bookingData.phone.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    if (!bookingData.name) {
      setError('Please enter your name');
      return;
    }

    if (bookingData.rentalType !== 'sale') {
      if (!bookingData.pickupLocation) {
        setError('Please enter pickup location');
        return;
      }
      if (!bookingData.dropoffLocation) {
        setError('Please enter drop-off location');
        return;
      }
      if (['km-based', 'outstation'].includes(bookingData.rentalType) && !bookingData.estimatedKm) {
        setError('Please enter estimated kilometers');
        return;
      }
    }

    // Show confirmation step
    setBookingStep('confirm');
  };

  // Final booking submission after confirmation
  const handleFinalBooking = async () => {
    setBookingLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/bookings/public`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          carId: selectedCar._id,
          phone: bookingData.phone,
          name: bookingData.name,
          startDate: bookingData.startDate || new Date().toISOString(),
          endDate: bookingData.endDate || new Date().toISOString(),
          pickupLocation: bookingData.pickupLocation,
          dropoffLocation: bookingData.dropoffLocation,
          estimatedKm: bookingData.estimatedKm ? parseInt(bookingData.estimatedKm) : 0,
          hours: bookingData.hours ? parseInt(bookingData.hours) : 0,
          notes: bookingData.notes,
          rentalType: bookingData.rentalType,
          offerPrice: bookingData.rentalType === 'sale' && bookingData.offerPrice ? parseInt(bookingData.offerPrice) : undefined
        })
      });

      const data = await response.json();

      if (data.success) {
        setBookingResult(data);
        setBookingModal(false);
        setSuccessModal(true);
      } else {
        setError(data.message || 'Request failed. Please try again.');
        setBookingStep('form');
      }
    } catch (error) {
      console.error('Booking error:', error);
      setError('Something went wrong. Please try again.');
      setBookingStep('form');
    } finally {
      setBookingLoading(false);
    }
  };

  const getTravelsInfo = (car) => {
    return car.ownerId?.travelsInfo || null;
  };

  const getPrice = (car) => {
    if (activeTab === 'sale') {
      return { amount: car.salePrice, suffix: '' };
    }
    if (activeTab === 'km-based') {
      return { amount: car.pricePerKm, suffix: '/km' };
    }
    if (activeTab === 'outstation') {
      return { amount: car.outstationPricePerKm || car.pricePerKm, suffix: '/km' };
    }
    return { amount: car.pricePerDay, suffix: '/day' };
  };

  return (
    <>
    <SEO title="Browse Vehicles" description="Browse & book cars, bikes, buses, trucks & more near you. Compare rates and book instantly." path="/browse" />
    <div className="fixed inset-0 flex flex-col bg-gray-100 overflow-hidden">
      {/* Fixed Background — single image, full height */}
      <div className="fixed inset-0 z-0">
        <img src="https://res.cloudinary.com/dfg39dktd/image/upload/v1779262734/car-booking/branding/browse-background.jpg" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-gray-100"></div>
      </div>

      {/* Navbar — hide on mobile when filter is open */}
      <nav className={`relative z-30 flex-shrink-0 bg-gray-900/90 backdrop-blur-md ${sidebarOpen ? 'md:block hidden' : ''}`}>
        <div className="max-w-7xl mx-auto px-3 py-2 md:px-4 md:py-3">
          <div className="flex items-center justify-between">
            <Link to={authUser ? '/dashboard' : '/home'} className="flex items-center space-x-2 hover:opacity-90 transition-opacity">
              <img src={appConfig.logo} alt={appConfig.name} className="h-8 w-8 md:h-9 md:w-9 object-contain rounded-lg" />
              <h1 className="text-base md:text-lg font-bold text-white truncate">{appConfig.name}</h1>
            </Link>
            <div className="flex items-center gap-2">
              {authUser ? (
                <>
                  {(authUser.role === 'owner' || authUser.role === 'admin' || authUser.role === 'manager') && (
                    <Link
                      to="/dashboard"
                      className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 bg-white/20 hover:bg-white/30 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors"
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      <span>Dashboard</span>
                    </Link>
                  )}
                  <Link
                    to="/track-booking"
                    className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-white/80 hover:text-white text-sm font-medium rounded-md transition-colors"
                  >
                    <Calendar className="h-4 w-4" />
                    My Bookings
                  </Link>
                  <div className="relative">
                    <button
                      onClick={() => setShowUserMenu(prev => !prev)}
                      className="flex items-center gap-1.5 px-2 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors"
                    >
                      <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                        <span className="text-xs font-bold text-white">
                          {(authUser.name?.startsWith('User ') ? authUser.phone?.slice(-2) : authUser.name?.charAt(0)?.toUpperCase()) || 'U'}
                        </span>
                      </div>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <button
                    onClick={() => navigate('/register')}
                    className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium rounded-lg transition-colors"
                  >
                    <UserPlus className="h-3.5 w-3.5" />
                    For Business
                  </button>
                  <button
                    onClick={() => navigate('/customer-login')}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-white/30 text-white hover:bg-white/10 text-xs font-medium rounded-lg transition-colors"
                  >
                    <LogIn className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Book Now</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Service group switcher — underline tabs (always visible at top, doesn't scroll).
          z-30 so its hover dropdowns appear above the search-bar (which sits at z-20). */}
      <div className={`relative z-30 flex-shrink-0 bg-white/95 backdrop-blur-md border-b border-gray-200 ${sidebarOpen ? 'md:block hidden' : ''}`}>
        <div className="max-w-7xl mx-auto px-3 md:px-4 flex items-center gap-2">
          {/* Compact Rent | Buy toggle (inline, same row) */}
          <div className="inline-flex p-1 bg-gray-200/70 rounded-xl flex-shrink-0 my-1.5 gap-1">
            <button
              onClick={goRent}
              className={`flex items-center gap-1.5 px-3.5 py-1 text-sm font-bold rounded-lg transition-all ${
                !buyMode
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-sm shadow-blue-500/30'
                  : 'bg-white text-gray-600 border border-gray-200 shadow-sm hover:text-blue-600 hover:border-blue-300'
              }`}
            >
              <Car className="h-3.5 w-3.5" /> Rent
            </button>
            <button
              onClick={goBuy}
              className={`flex items-center gap-1.5 px-3.5 py-1 text-sm font-bold rounded-lg transition-all ${
                buyMode
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-sm shadow-orange-500/30'
                  : 'bg-white text-gray-600 border border-gray-200 shadow-sm hover:text-orange-600 hover:border-orange-300'
              }`}
            >
              <ShoppingCart className="h-3.5 w-3.5" /> Buy
            </button>
          </div>
          <div className="flex gap-1 sm:gap-2 overflow-x-auto md:overflow-visible scrollbar-hide flex-1">
            {!buyMode && [
              { id: 'rental', label: 'Rental', icon: Car, defaultTab: 'daily' },
              { id: 'oneway', label: 'One-Way', icon: ArrowRightLeft, defaultTab: 'oneway' },
              { id: 'packages', label: 'Packages', icon: Calendar, defaultTab: 'all' },
            ].map(g => {
              const active = serviceGroup === g.id;
              // Sub-tabs for this group (Rental has 5, others have 1)
              const subTabsForThisGroup = SERVICE_TABS.filter(t => (SERVICE_GROUPS[g.id] || []).includes(t.id));
              const hasSubOptions = subTabsForThisGroup.length > 1;
              return (
                <div
                  key={g.id}
                  className="relative md:my-1.5"
                  onMouseEnter={() => hasSubOptions && setHoveredGroup(g.id)}
                  onMouseLeave={() => setHoveredGroup(null)}
                >
                  <button
                    onClick={() => {
                      setActiveTab(g.defaultTab);
                      setSearchParams({ service: g.id, tab: g.defaultTab });
                    }}
                    className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 md:py-1.5 text-sm font-semibold whitespace-nowrap rounded-lg transition-all duration-200 ${
                      hoveredGroup === g.id
                        ? 'bg-blue-50 text-blue-700'
                        : active
                          ? 'text-blue-600 hover:bg-gray-50'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <g.icon className="h-4 w-4" />
                    {g.label}
                    {/* Chevron-down hint when there are sub-options */}
                    {hasSubOptions && (
                      <ChevronRight className={`hidden md:inline h-3.5 w-3.5 rotate-90 transition-transform duration-200 ${hoveredGroup === g.id ? '-rotate-90' : ''} ${active || hoveredGroup === g.id ? 'text-blue-500' : 'text-gray-400'}`} />
                    )}
                  </button>

                  {/* DESKTOP — Hover dropdown showing sub-options */}
                  {hasSubOptions && (
                    <div
                      className={`hidden md:block absolute top-full left-0 mt-0.5 min-w-[200px] bg-white rounded-xl shadow-2xl border border-gray-100 py-2 z-50 transition-all duration-150 origin-top ${
                        hoveredGroup === g.id ? 'opacity-100 scale-100 visible' : 'opacity-0 scale-95 invisible pointer-events-none'
                      }`}
                    >
                      {subTabsForThisGroup.map(tab => {
                        const TabIcon = tab.icon;
                        const isActiveSub = activeTab === tab.id && active;
                        return (
                          <button
                            key={tab.id}
                            onClick={() => {
                              setActiveTab(tab.id);
                              setSearchParams({ service: g.id, tab: tab.id });
                              setHoveredGroup(null);
                            }}
                            className={`flex items-center gap-2.5 w-full px-4 py-2.5 text-sm font-medium transition-colors ${
                              isActiveSub
                                ? 'bg-blue-50 text-blue-700'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                            }`}
                          >
                            <span className={`flex items-center justify-center w-7 h-7 rounded-lg ${isActiveSub ? 'bg-blue-100' : 'bg-gray-100'}`}>
                              <TabIcon className={`h-3.5 w-3.5 ${isActiveSub ? 'text-blue-600' : 'text-gray-500'}`} />
                            </span>
                            <span className="flex-1 text-left">{tab.label}</span>
                            {isActiveSub && <CheckCircle2 className="h-4 w-4 text-blue-600 flex-shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* DESKTOP — Location pill on the right side of the service tabs row */}
          {locationLoading ? (
            <span className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full ml-3 flex-shrink-0">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />
              <span className="text-xs font-medium text-gray-600">Detecting…</span>
            </span>
          ) : detectedCity ? (
            <button
              onClick={() => setShowLocationSearch(prev => !prev)}
              title={showLocationSearch ? 'Tap to close' : 'Tap to change location'}
              className={`hidden md:inline-flex items-center gap-1.5 pl-2 pr-3 py-1.5 border rounded-full transition-all ml-3 flex-shrink-0 ${
                showLocationSearch
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-blue-50 hover:bg-blue-100 border-blue-200 hover:border-blue-300 text-blue-700'
              }`}
            >
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${showLocationSearch ? 'bg-white' : 'bg-blue-600'}`}>
                <MapPin className={`h-3 w-3 ${showLocationSearch ? 'text-blue-600' : 'text-white'}`} />
              </span>
              <span className="text-xs font-bold truncate max-w-[140px]">{detectedCity}</span>
              <ChevronRight className={`h-3 w-3 transition-transform duration-200 ${showLocationSearch ? '-rotate-90 text-white/80' : 'rotate-90 text-blue-500'}`} />
            </button>
          ) : (
            <button
              onClick={() => setShowLocationSearch(prev => !prev)}
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-full text-white text-xs font-semibold transition-all ml-3 flex-shrink-0"
            >
              <MapPin className="h-3.5 w-3.5" />
              Set Location
            </button>
          )}
        </div>
      </div>

      {/* Tabs Bar — hide on mobile when filter is open. Also hide entirely when
          the active service has only one sub-option (no point taking space). */}
      {/* Sub-tabs bar — MOBILE ONLY now (desktop uses hover dropdowns on parent tabs) */}
      <div className={`md:hidden relative z-20 flex-shrink-0 bg-gray-900/70 backdrop-blur-sm ${sidebarOpen ? 'hidden' : ''} ${buyMode || visibleTabs.filter((t) => t.id !== 'sale').length <= 1 ? 'hidden' : ''}`}>
        <div className="max-w-7xl mx-auto px-3">
          <div className="flex overflow-x-auto gap-1.5 py-2 scrollbar-hide">
            {visibleTabs.filter((tab) => tab.id !== 'sale').map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full whitespace-nowrap text-xs font-medium transition-colors ${
                    isActive
                      ? 'bg-white text-gray-900 shadow-lg'
                      : 'bg-white/15 text-white hover:bg-white/25 backdrop-blur-sm'
                  }`}
                >
                  <Icon className="h-3 w-3" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Search Bar — hide on mobile when filter is open. Hide entirely on Packages
          (Packages has its own dedicated From/To/Date search bar inside main).
          Also collapse smoothly when user scrolls down.
          Also hide when the manual location-search input is open
          (otherwise two cards stack — old detected + new search). */}
      <div className={`relative z-10 sm:z-20 flex-shrink-0 px-0 md:px-4 transition-all duration-300 ease-out ${hideTopBar ? 'max-h-0 pt-0 pb-0 opacity-0 overflow-hidden' : `pt-2 pb-2 opacity-100 ${(activeTab !== 'oneway' && activeTab !== 'outstation') ? 'md:pt-0 md:pb-0 md:max-h-0 md:overflow-hidden' : ''}`} ${sidebarOpen ? 'md:block hidden' : ''} ${serviceGroup === 'packages' ? 'hidden' : ''} ${showLocationSearch ? 'hidden' : ''}`}>
        <div className="max-w-7xl mx-auto">
          <div className={`${(activeTab === 'oneway' || activeTab === 'outstation') ? 'bg-white rounded-xl shadow-lg overflow-hidden md:bg-white/75 md:backdrop-blur-md' : ''}`}>
            {activeTab === 'oneway' || activeTab === 'outstation' ? (
              <div className="flex flex-col sm:flex-row relative">
                <div className="flex-1 flex items-center gap-3 px-4 py-2.5 border-b sm:border-b-0 sm:border-r border-gray-200">
                  <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></div>
                  <CityAutocompleteInline
                    value={activeTab === 'oneway' ? onewayFrom : outstationFrom}
                    onChange={(v) => activeTab === 'oneway' ? setOnewayFrom(v) : setOutstationFrom(v)}
                    placeholder="Pickup City"
                    inputClassName="w-full text-gray-900 placeholder-gray-600 focus:outline-none text-sm bg-transparent capitalize font-medium border-0 p-0"
                    onKeyDown={(e) => e.key === 'Enter' && (activeTab === 'oneway' ? searchOnewayTrips() : searchOutstationCabs())}
                  />
                </div>
                {/* Swap From <-> To */}
                <button
                  type="button"
                  onClick={() => {
                    if (activeTab === 'oneway') {
                      const t = onewayFrom; setOnewayFrom(onewayTo); setOnewayTo(t);
                    } else {
                      const t = outstationFrom; setOutstationFrom(outstationTo); setOutstationTo(t);
                    }
                  }}
                  title="Swap From and To"
                  aria-label="Swap From and To"
                  className="absolute left-1/2 sm:left-[33.33%] top-[calc(2rem)] sm:top-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white border border-gray-300 shadow-md text-blue-600 hover:text-blue-700 hover:bg-blue-50 hover:rotate-180 transition-all duration-300 flex items-center justify-center z-10"
                >
                  <ArrowRightLeft className="h-3.5 w-3.5 rotate-90 sm:rotate-0" />
                </button>
                <div className="flex-1 flex items-center gap-3 px-4 py-2.5 border-b sm:border-b-0 sm:border-r border-gray-200">
                  <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"></div>
                  <CityAutocompleteInline
                    value={activeTab === 'oneway' ? onewayTo : outstationTo}
                    onChange={(v) => activeTab === 'oneway' ? setOnewayTo(v) : setOutstationTo(v)}
                    placeholder="Drop City"
                    inputClassName="w-full text-gray-900 placeholder-gray-600 focus:outline-none text-sm bg-transparent capitalize font-medium border-0 p-0"
                    onKeyDown={(e) => e.key === 'Enter' && (activeTab === 'oneway' ? searchOnewayTrips() : searchOutstationCabs())}
                  />
                </div>
                <div className="flex-none flex items-center gap-2 px-4 py-2.5 border-b sm:border-b-0 sm:border-r border-gray-200">
                  <Calendar className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <input
                    type="date"
                    value={activeTab === 'oneway' ? onewayDate : outstationDate}
                    onChange={(e) => activeTab === 'oneway' ? setOnewayDate(e.target.value) : setOutstationDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="text-gray-900 focus:outline-none text-sm bg-transparent"
                  />
                </div>
                <button
                  onClick={activeTab === 'oneway' ? searchOnewayTrips : searchOutstationCabs}
                  disabled={activeTab === 'oneway' ? (!onewayFrom.trim() || !onewayTo.trim()) : (!outstationFrom.trim() || !outstationTo.trim())}
                  className="flex items-center justify-center gap-2 px-5 py-3 sm:py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition-colors disabled:opacity-40 w-full sm:w-auto rounded-b-xl sm:rounded-none sm:rounded-r-xl shadow-md"
                >
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            ) : (
              <>
                {/* ───────── MOBILE — unified card with Location | Radius | Filters ───────── */}
                <div className="md:hidden px-2">
                  {locationLoading ? (
                    <div className="flex items-center justify-center gap-2 px-4 py-3 bg-white rounded-2xl shadow-md border border-gray-200">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                      <span className="text-sm font-medium text-gray-600">Detecting your location…</span>
                    </div>
                  ) : !detectedCity ? (
                    <button
                      onClick={() => setShowLocationSearch(true)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl shadow-md text-sm font-semibold transition-all"
                    >
                      <MapPin className="w-4 h-4" />
                      Set your location
                    </button>
                  ) : (
                    <div className="flex items-stretch bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                      <button
                        onClick={() => setShowLocationSearch(true)}
                        title="Tap to change location"
                        className="flex-1 flex items-center gap-2 pl-2.5 pr-1.5 py-1.5 hover:bg-gray-50 transition-colors text-left min-w-0"
                      >
                        <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-3 h-3 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[8px] uppercase tracking-wider text-gray-400 font-semibold leading-none">Location</p>
                          <p className="text-xs font-bold text-gray-900 truncate leading-tight mt-0.5">{detectedCity}</p>
                        </div>
                      </button>
                      {userLocation && (
                        <>
                          <span className="w-px bg-gray-200 my-1.5 flex-shrink-0" />
                          <button
                            onClick={() => {
                              const cycle = [1, 2, 3, 5, 10, 25, 50];
                              const idx = cycle.indexOf(searchRadius);
                              const next = idx === -1 ? 3 : cycle[(idx + 1) % cycle.length];
                              setSearchRadius(next);
                              setRadiusManuallySet(true);
                            }}
                            title="Tap to cycle radius"
                            className="flex items-center px-2.5 py-1.5 hover:bg-gray-50 transition-colors flex-shrink-0"
                          >
                            <div className="text-right">
                              <p className="text-[8px] uppercase tracking-wider text-gray-400 font-semibold leading-none">Radius</p>
                              <p className="text-xs font-bold text-gray-900 leading-tight whitespace-nowrap mt-0.5">{searchRadius} km</p>
                            </div>
                          </button>
                        </>
                      )}
                      {(() => {
                        const filterCount = [
                          selectedVehicleType !== 'all',
                          filterFuelType !== 'all',
                          filterTransmission !== 'all',
                          filterSeats !== 'all',
                          priceActive,
                          ...filterFeatures.map(() => true),
                          searchTerm,
                        ].filter(Boolean).length;
                        return (
                          <button
                            onClick={() => setSidebarOpen(true)}
                            title="Open filters"
                            aria-label="Filters"
                            className="relative flex flex-col items-center justify-center gap-0.5 w-14 py-1.5 bg-blue-600 hover:bg-blue-700 text-white transition-colors flex-shrink-0"
                          >
                            <SlidersHorizontal className="w-4 h-4" />
                            <span className="text-[9px] font-semibold uppercase tracking-wide leading-none">Filter</span>
                            {filterCount > 0 && (
                              <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-yellow-300 text-blue-700 text-[10px] font-black flex items-center justify-center ring-2 ring-blue-600 shadow-md">
                                {filterCount}
                              </span>
                            )}
                          </button>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Desktop intentionally has no top bar here — the sidebar on
                    the left already shows Location and Search Radius. Hiding
                    this saves the wasted vertical band you saw on wide screens. */}
              </>
            )}
          </div>
        </div>
      </div>


      {/* Location Search Bar — also hide on Packages (uses its own From input) */}
      {showLocationSearch && serviceGroup !== 'packages' && (
        <div className="relative z-20 px-4 pb-2">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow-md border border-gray-200 px-3 py-2 sm:px-4 sm:py-3">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <AddressAutocomplete
                    value=""
                    label=""
                    placeholder="Enter city or area..."
                    onChange={(loc) => {
                      if (loc.lat && loc.lng) {
                        setUserLocation({ latitude: loc.lat, longitude: loc.lng });
                        setDetectedCity(loc.city || loc.address?.split(',')[0] || '');
                        setLocationError('');
                        setLocationDone(true);
                        setSortBy('nearest');
                        setShowLocationSearch(false);
                      }
                    }}
                    onClear={() => {
                      setUserLocation(null);
                      setDetectedCity('');
                      setLocationDone(true);
                      setSortBy('default');
                      setShowLocationSearch(false);
                    }}
                  />
                </div>
                <button
                  onClick={() => {
                    setShowLocationSearch(false);
                    setUserLocation(null);
                    setDetectedCity('');
                    setLocationDone(false);
                    detectLocation();
                  }}
                  className="px-2 py-1.5 sm:px-3 sm:py-2 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg text-[10px] sm:text-xs font-medium flex items-center gap-1 whitespace-nowrap flex-shrink-0"
                >
                  <Locate className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  <span className="hidden sm:inline">Auto Detect</span>
                  <span className="sm:hidden">Auto</span>
                </button>
                <button
                  onClick={() => setShowLocationSearch(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 flex-shrink-0"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="relative z-10 flex-1 min-h-0">
        <div className="w-full h-full md:h-[calc(100vh-140px)]">
        <div className="flex relative h-full min-h-0">

          {/* Mobile Filter Toggle Button - removed fixed, now in sticky bar */}

          {/* Mobile Filter Overlay — blocks entire page */}
          <div
            className={`fixed inset-0 bg-black/50 z-[9998] md:hidden transition-opacity duration-300 ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            onClick={() => setSidebarOpen(false)}
          />

          {/* LEFT SIDEBAR — Filters */}
          {/* Desktop: side panel */}
          <aside className={`${serviceGroup === 'packages' ? 'hidden' : 'hidden md:block'} w-64 flex-shrink-0 bg-white border-r border-gray-200 z-10 h-full overflow-y-auto rounded-tl-xl shadow-sm`}>
            <div className="sticky top-0 z-10 bg-white/95 backdrop-blur px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4 text-blue-600" />
              <h3 className="text-sm font-bold text-gray-900 tracking-tight">Filters</h3>
            </div>
            <div className="px-4 divide-y divide-gray-100">
              {/* Search Radius - Desktop */}
              {userLocation && (
                <div className="py-3">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className={sectionLabelCls + ' !mb-0'}>
                      <Navigation className="h-4 w-4 text-blue-500" />
                      Search Radius
                    </h4>
                    <span className="text-xs font-bold text-blue-600">{searchRadius} km</span>
                  </div>
                  {(() => {
                    const config = getRadiusConfig(selectedVehicleType);
                    const rMin = Math.min(...config.buttons);
                    const rMax = Math.max(...config.buttons, ...(config.dropdown || []));
                    return (
                      <RadiusSlider
                        min={rMin}
                        max={rMax}
                        value={Math.min(Math.max(searchRadius, rMin), rMax)}
                        onChange={(km) => { setSearchRadius(km); setRadiusManuallySet(true); }}
                      />
                    );
                  })()}
                  <p className="text-xs text-gray-500 mt-1">
                    {radiusFilteredCars.length} found within {searchRadius} km
                  </p>
                </div>
              )}

              {/* Vehicle Types - Desktop */}
              <div className="py-3">
                <h4 className={sectionLabelCls}>
                  <Car className="h-4 w-4 text-blue-500" />
                  Vehicle Type
                </h4>
                <select
                  value={selectedVehicleType}
                  onChange={(e) => setSelectedVehicleType(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-gray-50 focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-colors outline-none"
                >
                  {vehicleTypeOptions.map((type) => (
                    <option key={type.value} value={type.value}>
                      {optionGlyph(type.icon) ? optionGlyph(type.icon) + ' ' : ''}{type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Fuel Type Filter */}
              <div className="py-3">
                <h4 className={sectionLabelCls}>
                  <Fuel className="h-4 w-4 text-blue-500" />
                  Fuel Type
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {['all', 'Petrol', 'Diesel', 'Electric', 'CNG', 'Hybrid'].map((fuel) => (
                    <button
                      key={fuel}
                      onClick={() => setFilterFuelType(fuel)}
                      className={pillCls(filterFuelType === fuel)}
                    >
                      {fuel === 'all' ? 'All' : fuel}
                    </button>
                  ))}
                </div>
              </div>

              {/* Transmission Filter */}
              <div className="py-3">
                <h4 className={sectionLabelCls}>
                  <Settings className="h-4 w-4 text-blue-500" />
                  Transmission
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {['all', 'Manual', 'Automatic'].map((t) => (
                    <button
                      key={t}
                      onClick={() => setFilterTransmission(t)}
                      className={pillCls(filterTransmission === t)}
                    >
                      {t === 'all' ? 'All' : t}
                    </button>
                  ))}
                </div>
              </div>

              {/* Seating Capacity */}
              <div className="py-3">
                <h4 className={sectionLabelCls}>
                  <Users className="h-4 w-4 text-blue-500" />
                  Seats
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { value: 'all', label: 'All' },
                    { value: '2', label: '1-2' },
                    { value: '4-5', label: '4-5' },
                    { value: '7+', label: '7+' },
                  ].map((s) => (
                    <button
                      key={s.value}
                      onClick={() => setFilterSeats(s.value)}
                      className={pillCls(filterSeats === s.value)}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="py-3">
                <div className="flex items-center justify-between mb-3">
                  <h4 className={sectionLabelCls + ' !mb-0'}>
                    <IndianRupee className="h-4 w-4 text-blue-500" />
                    {activeTab === 'sale' ? 'Price' : 'Price / Day'}
                  </h4>
                  {priceActive && (
                    <button onClick={() => setPriceRange(null)} className="text-xs font-semibold text-blue-600 hover:text-blue-700">
                      Reset
                    </button>
                  )}
                </div>
                <PriceRangeSlider
                  min={priceBounds.min}
                  max={priceBounds.max}
                  step={priceBounds.step}
                  value={priceRange || [priceBounds.min, priceBounds.max]}
                  onChange={setPriceRange}
                  format={(v) => fmtPrice(v, activeTab)}
                />
              </div>

              {/* Used-vehicle filters — only meaningful on the Buy tab */}
              {isBuyTab && (
                <div className="my-4 rounded-xl border border-gray-100 bg-gray-50/60 p-3">
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="text-sm font-bold text-gray-900">Used vehicle</h4>
                    {buyActiveCount > 0 && (
                      <button onClick={clearBuyFilters} className="text-xs font-semibold text-blue-600 hover:text-blue-700">
                        Clear ({buyActiveCount})
                      </button>
                    )}
                  </div>
                  {BUY_FILTERS.map((group) => (
                    <BuyFilterGroup
                      key={group.key}
                      group={group}
                      value={buyFilters[group.key]}
                      onChange={setBuyFilter}
                      counts={buyOptionCounts}
                      defaultOpen
                    />
                  ))}
                </div>
              )}

              {/* Features Filter */}
              <div className="py-3">
                <h4 className={sectionLabelCls}>
                  <Sparkles className="h-4 w-4 text-blue-500" />
                  Features
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {['AC', 'GPS', 'Bluetooth', 'Airbags', 'ABS', 'Sunroof', 'Power Steering', 'Rear Camera', 'Music System', 'Helmet Included'].map((f) => (
                    <button
                      key={f}
                      onClick={() => {
                        setFilterFeatures(prev =>
                          prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f]
                        );
                      }}
                      className={`px-2 py-1 text-[10px] font-medium rounded-full border transition-colors ${
                        filterFeatures.includes(f)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-500 border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      {filterFeatures.includes(f) ? '✓ ' : ''}{f}
                    </button>
                  ))}
                </div>
              </div>

              {/* Active Filters Summary - Desktop */}
              {(selectedVehicleType !== 'all' || activeTab !== 'all' || searchTerm || filterFuelType !== 'all' || filterTransmission !== 'all' || filterSeats !== 'all' || priceActive || filterFeatures.length > 0) && (
                <div className="py-3">
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {activeTab !== 'all' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                        {SERVICE_TABS.find(t => t.id === activeTab)?.label}
                        <button onClick={() => setActiveTab('all')} className="hover:bg-blue-200 rounded-full p-0.5">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {selectedVehicleType !== 'all' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                        {VEHICLE_TYPES.find(v => v.value === selectedVehicleType)?.label}
                        <button onClick={() => setSelectedVehicleType('all')} className="hover:bg-orange-200 rounded-full p-0.5">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {searchTerm && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                        "{searchTerm}"
                        <button onClick={() => setSearchTerm('')} className="hover:bg-purple-200 rounded-full p-0.5">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    )}
                    {filterFuelType !== 'all' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                        {filterFuelType}
                        <button onClick={() => setFilterFuelType('all')} className="hover:bg-green-200 rounded-full p-0.5"><X className="h-3 w-3" /></button>
                      </span>
                    )}
                    {filterTransmission !== 'all' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
                        {filterTransmission}
                        <button onClick={() => setFilterTransmission('all')} className="hover:bg-indigo-200 rounded-full p-0.5"><X className="h-3 w-3" /></button>
                      </span>
                    )}
                    {filterSeats !== 'all' && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full">
                        {filterSeats} seats
                        <button onClick={() => setFilterSeats('all')} className="hover:bg-teal-200 rounded-full p-0.5"><X className="h-3 w-3" /></button>
                      </span>
                    )}
                    {priceActive && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                        {priceLabel}
                        <button onClick={() => setPriceRange(null)} className="hover:bg-amber-200 rounded-full p-0.5"><X className="h-3 w-3" /></button>
                      </span>
                    )}
                    {filterFeatures.map(f => (
                      <span key={f} className="inline-flex items-center gap-1 px-2 py-1 bg-cyan-100 text-cyan-700 text-xs font-medium rounded-full">
                        {f}
                        <button onClick={() => setFilterFeatures(prev => prev.filter(x => x !== f))} className="hover:bg-cyan-200 rounded-full p-0.5"><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedVehicleType('all');
                      setSortBy('default');
                      setActiveTab(defaultTab);
                      setFilterFuelType('all');
                      setFilterTransmission('all');
                      setFilterSeats('all');
                      setPriceRange(null);
                      setFilterFeatures([]);
                      setFilterAvailability('all');
                      setRadiusManuallySet(false);
                    }}
                    className="w-full py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 font-medium rounded-lg transition-colors border border-red-200"
                  >
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>
          </aside>


          {/* MAIN CONTENT AREA */}
          <main ref={mainScrollRef} className="flex-1 min-w-0 min-h-0 relative z-10 bg-gray-50 md:rounded-tr-xl h-full overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            {serviceGroup === 'packages' ? (
              <div className="p-4 sm:p-6">
                {/* Package search bar — From / To / Date */}
                <datalist id="pkg-city-list">
                  {packageCities.map(c => <option key={c} value={c} />)}
                </datalist>
                {/* Modern designed search card — colored icon badges + soft gradient */}
                <div className="relative bg-gradient-to-br from-blue-50/60 via-white to-indigo-50/40 rounded-2xl border border-gray-200 shadow-md mb-4 overflow-hidden">
                  {/* Decorative blob */}
                  <div className="absolute -top-12 -right-12 w-48 h-48 bg-blue-200/20 rounded-full blur-3xl pointer-events-none" />
                  <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-indigo-200/20 rounded-full blur-3xl pointer-events-none" />

                  <div className="relative flex flex-col lg:flex-row lg:items-stretch">
                    {/* FROM */}
                    <label className="flex-1 px-4 py-3 cursor-text hover:bg-white/50 transition-colors relative">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-100 ring-1 ring-blue-200 flex items-center justify-center flex-shrink-0">
                          <MapPin className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider mb-0.5">From</p>
                          <CityAutocompleteInline
                            value={pkgFrom}
                            onChange={setPkgFrom}
                            placeholder="Bangalore"
                            inputClassName="w-full text-sm font-bold text-gray-900 placeholder-gray-400 bg-transparent border-0 p-0 focus:outline-none focus:ring-0"
                            onKeyDown={(e) => e.key === 'Enter' && applyPkgFilters()}
                          />
                        </div>
                      </div>
                    </label>

                    {/* Connector + swap button (mobile horizontal | desktop vertical) */}
                    <div className="relative flex justify-center lg:hidden">
                      <div className="absolute inset-y-0 left-9 w-px border-l-2 border-dashed border-gray-200 -translate-y-3" />
                      <button
                        type="button"
                        onClick={() => { const t = pkgFrom; setPkgFrom(pkgTo); setPkgTo(t); }}
                        title="Swap From and To"
                        aria-label="Swap From and To"
                        className="relative ml-4 w-8 h-8 rounded-full bg-white border border-gray-200 shadow text-gray-600 hover:text-blue-600 hover:border-blue-300 hover:rotate-180 transition-all duration-300 flex items-center justify-center -my-1.5"
                      >
                        <ArrowRightLeft className="h-3.5 w-3.5 rotate-90" />
                      </button>
                    </div>

                    {/* Desktop swap button */}
                    <button
                      type="button"
                      onClick={() => { const t = pkgFrom; setPkgFrom(pkgTo); setPkgTo(t); }}
                      title="Swap From and To"
                      aria-label="Swap From and To"
                      className="hidden lg:flex absolute left-[20%] top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white border border-gray-200 shadow-md text-gray-600 hover:text-blue-600 hover:border-blue-300 hover:rotate-180 transition-all duration-300 items-center justify-center"
                    >
                      <ArrowRightLeft className="h-3.5 w-3.5" />
                    </button>

                    {/* TO */}
                    <label className="flex-1 px-4 py-3 cursor-text hover:bg-white/50 transition-colors border-t lg:border-t-0 lg:border-l border-gray-100">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-100 ring-1 ring-emerald-200 flex items-center justify-center flex-shrink-0">
                          <MapPin className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-0.5">To</p>
                          <CityAutocompleteInline
                            value={pkgTo}
                            onChange={setPkgTo}
                            placeholder="Tirupati"
                            inputClassName="w-full text-sm font-bold text-gray-900 placeholder-gray-400 bg-transparent border-0 p-0 focus:outline-none focus:ring-0"
                            onKeyDown={(e) => e.key === 'Enter' && applyPkgFilters()}
                          />
                        </div>
                      </div>
                    </label>

                    {/* Travel Date */}
                    <label className="flex-1 px-4 py-3 cursor-pointer hover:bg-white/50 transition-colors border-t lg:border-t-0 lg:border-l border-gray-100">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-purple-100 ring-1 ring-purple-200 flex items-center justify-center flex-shrink-0">
                          <Calendar className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-bold text-purple-600 uppercase tracking-wider mb-0.5">Travel Date</p>
                          <input
                            type="date"
                            value={pkgDate}
                            min={new Date().toISOString().slice(0, 10)}
                            onChange={(e) => setPkgDate(e.target.value)}
                            className="w-full text-sm font-bold text-gray-900 placeholder-gray-400 bg-transparent border-0 p-0 focus:outline-none focus:ring-0"
                          />
                        </div>
                      </div>
                    </label>

                    {/* Duration */}
                    <label className="flex-1 px-4 py-3 cursor-pointer hover:bg-white/50 transition-colors border-t lg:border-t-0 lg:border-l border-gray-100">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-amber-100 ring-1 ring-amber-200 flex items-center justify-center flex-shrink-0">
                          <Clock className="h-5 w-5 text-amber-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider mb-0.5">Duration</p>
                          <select
                            value={pkgDuration}
                            onChange={(e) => setPkgDuration(e.target.value)}
                            className="w-full text-sm font-bold text-gray-900 bg-transparent border-0 p-0 focus:outline-none focus:ring-0 appearance-none cursor-pointer pr-4"
                          >
                            <option value="all">Any duration</option>
                            <option value="1">1 day</option>
                            <option value="2-3">2–3 days</option>
                            <option value="4+">4+ days</option>
                          </select>
                        </div>
                      </div>
                    </label>

                    {/* Budget */}
                    <label className="flex-1 px-4 py-3 cursor-pointer hover:bg-white/50 transition-colors border-t lg:border-t-0 lg:border-l border-gray-100">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl bg-rose-100 ring-1 ring-rose-200 flex items-center justify-center flex-shrink-0">
                          <Gauge className="h-5 w-5 text-rose-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-bold text-rose-600 uppercase tracking-wider mb-0.5">Budget</p>
                          <select
                            value={pkgPriceMax}
                            onChange={(e) => setPkgPriceMax(e.target.value)}
                            className="w-full text-sm font-bold text-gray-900 bg-transparent border-0 p-0 focus:outline-none focus:ring-0 appearance-none cursor-pointer pr-4"
                          >
                            <option value="all">Any budget</option>
                            <option value="5000">≤ ₹5,000</option>
                            <option value="10000">≤ ₹10,000</option>
                            <option value="20000">≤ ₹20,000</option>
                          </select>
                        </div>
                      </div>
                    </label>

                    {/* Search button — gradient, prominent */}
                    <div className="p-3 lg:p-2.5 flex border-t lg:border-t-0 lg:border-l border-gray-100">
                      <button
                        onClick={applyPkgFilters}
                        className={`group flex-1 lg:flex-none inline-flex items-center justify-center gap-2 px-6 py-3 lg:py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-bold rounded-xl transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 whitespace-nowrap ${
                          pkgHasUnappliedChanges ? 'ring-2 ring-blue-300 animate-pulse' : ''
                        }`}
                      >
                        <Search className="h-4 w-4" />
                        Search
                      </button>
                    </div>
                  </div>

                  {/* Status footer — count + clear */}
                  <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between flex-wrap gap-2 bg-gray-50/50">
                    <span className="text-xs text-gray-600">
                      {pkgFiltersActive ? (
                        <><span className="font-semibold text-gray-900">{filteredPackages.length}</span> {filteredPackages.length === 1 ? 'package' : 'packages'} found</>
                      ) : (
                        <span className="text-gray-400">Enter your route and tap Search to filter</span>
                      )}
                    </span>
                    {pkgFiltersActive && (
                      <button
                        onClick={clearPkgFilters}
                        className="text-xs text-gray-600 hover:text-gray-900 font-medium flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                      >
                        <X className="h-3 w-3" /> Clear filters
                      </button>
                    )}
                  </div>
                  {pkgDate && (
                    <p className="mt-2 text-[11px] text-gray-500 italic">
                      💡 Packages are recurring — book your preferred date with the agency after enquiring.
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">Tour Packages</h2>
                  {detectedCity && !pkgFiltersActive && (
                    <span className="inline-flex items-center gap-1 text-xs text-gray-600 bg-blue-50 px-2.5 py-1 rounded-full">
                      <MapPin className="h-3.5 w-3.5 text-blue-600" />
                      Showing packages near <span className="font-semibold">{detectedCity}</span> first
                    </span>
                  )}
                </div>
                {filteredPackages.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
                    <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">
                      {pkgFiltersActive ? 'No packages match your search.' : 'No packages available yet.'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {pkgFiltersActive ? 'Try clearing filters or changing the cities.' : "Travel agencies haven't published packages here yet."}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredPackages.map(pkg => {
                      const finalPrice = pkg.basePrice - (pkg.basePrice * (pkg.discountPercentage || 0) / 100);
                      const route = [pkg.startPoint, ...(pkg.places || [])].filter(Boolean).join(' → ');
                      const ownerName = pkg.ownerId?.travelsInfo?.name || pkg.ownerId?.name || 'Travels Agency';
                      const logo = pkg.ownerId?.travelsInfo?.logo?.url;
                      const fromCity = isFromCity(pkg);
                      return (
                        <Link key={pkg._id} to={`/travels/${pkg.ownerId?._id}`}
                          className={`group relative bg-white rounded-xl border ${fromCity ? 'border-blue-300 ring-1 ring-blue-100' : 'border-gray-200'} hover:border-gray-300 hover:shadow-md transition-all duration-200 flex flex-col overflow-hidden`}>
                          {/* Header — agency strip with NEAR YOU badge */}
                          <div className="flex items-center gap-3 p-4 pb-3 border-b border-gray-100">
                            <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center flex-shrink-0 border border-gray-100">
                              {logo ? <img src={logo} alt="" className="w-full h-full object-cover" /> : <Calendar className="h-5 w-5 text-gray-400" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="text-sm font-semibold text-gray-900 truncate leading-tight">{pkg.name}</h3>
                              <p className="text-xs text-gray-500 truncate">{ownerName}</p>
                            </div>
                            {fromCity && (
                              <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 bg-blue-50 text-blue-700 rounded whitespace-nowrap flex-shrink-0 border border-blue-100">Near You</span>
                            )}
                          </div>

                          {/* Body */}
                          <div className="p-4 flex-1 flex flex-col">
                            {/* Route */}
                            {route && (
                              <div className="flex items-start gap-2 text-sm text-gray-800 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2.5 mb-3">
                                <MapPin className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                                <span className="font-medium leading-snug">{route}</span>
                              </div>
                            )}

                            {/* Stats row */}
                            <div className="flex items-center gap-4 mb-3 text-xs text-gray-600">
                              <div className="flex items-center gap-1.5">
                                <Clock className="h-3.5 w-3.5 text-gray-400" />
                                <span>{pkg.duration?.value} {pkg.duration?.unit}</span>
                              </div>
                              {pkg.includedKm > 0 && (
                                <div className="flex items-center gap-1.5">
                                  <Gauge className="h-3.5 w-3.5 text-gray-400" />
                                  <span>{pkg.includedKm} km included</span>
                                </div>
                              )}
                            </div>

                            {/* Description */}
                            {pkg.description && (
                              <p className="text-xs text-gray-500 line-clamp-2 mb-3">{pkg.description}</p>
                            )}

                            {/* Price footer */}
                            <div className="mt-auto pt-3 border-t border-gray-100">
                              <div className="flex items-end justify-between gap-3">
                                <div>
                                  <div className="flex items-baseline gap-2">
                                    <span className="text-xl font-bold text-gray-900">{formatCurrency(finalPrice)}</span>
                                    {pkg.discountPercentage > 0 && (
                                      <span className="text-xs text-gray-400 line-through">{formatCurrency(pkg.basePrice)}</span>
                                    )}
                                  </div>
                                  {pkg.discountPercentage > 0 && (
                                    <span className="text-[11px] font-medium text-emerald-700">Save {pkg.discountPercentage}%</span>
                                  )}
                                </div>
                                <div className="inline-flex items-center gap-1 px-3.5 py-2 bg-blue-600 group-hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors">
                                  View
                                  <ChevronRight className="h-3.5 w-3.5" />
                                </div>
                              </div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <>
            {/* Compact result toolbar — sticky on mobile. Single row:
                [count] ··· [view toggle] [sort] */}
            <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-md px-3 py-2 md:px-4 border-b border-gray-100">
              {/* Count + view/sort row */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  {/* Count + Refresh together — always on the same line, never wrap */}
                  <div className="inline-flex items-center gap-1.5 flex-shrink-0">
                    <span className="text-sm font-bold text-gray-900 whitespace-nowrap">
                      <span className="text-blue-600">{activeTab === 'oneway' ? onewayTrips.length : radiusFilteredCars.length}</span>{' '}
                      {activeTab === 'sale' ? 'For Sale' : activeTab === 'oneway' ? 'Trips' : 'Vehicles'}
                    </span>
                    <button
                      onClick={handleRefresh}
                      disabled={loading}
                      className="p-1 text-gray-400 hover:text-blue-600 rounded-full transition-colors disabled:opacity-50"
                      title="Refresh"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>

                  {/* Active sub-tab indicator — appears after count + refresh.
                      Wraps to next line if there isn't space. */}
                  {(() => {
                    const subTabsForGroup = SERVICE_TABS.filter(t => (SERVICE_GROUPS[serviceGroup] || []).includes(t.id));
                    if (subTabsForGroup.length <= 1) return null;
                    const activeSubTab = SERVICE_TABS.find(t => t.id === activeTab);
                    if (!activeSubTab) return null;
                    const SubIcon = activeSubTab.icon;
                    return (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full border border-blue-100 whitespace-nowrap">
                        <SubIcon className="h-3 w-3" />
                        {activeSubTab.label}
                      </span>
                    );
                  })()}
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {/* View Toggle — colored standard segmented control, hide for one-way */}
                  <div className={`${activeTab === 'oneway' ? 'hidden' : 'flex'} bg-gray-100 rounded-lg p-0.5`}>
                    <button
                      onClick={() => setViewMode('list')}
                      title="List view"
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                        viewMode === 'list'
                          ? 'bg-white text-blue-700 shadow-sm'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <List className={`w-3.5 h-3.5 ${viewMode === 'list' ? 'text-blue-600' : 'text-gray-400'}`} />
                      List
                    </button>
                    <button
                      onClick={() => setViewMode('map')}
                      title="Map view"
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                        viewMode === 'map'
                          ? 'bg-gradient-to-br from-emerald-50 to-emerald-100 text-emerald-800 shadow-sm ring-1 ring-emerald-200'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {/* Mini colored map icon — green land, blue water, yellow road, red pin */}
                      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="16" height="16" rx="2.5" fill="#dcfce7" />
                        <path d="M0 11 C 4 9, 9 12, 16 9.5 L 16 16 L 0 16 Z" fill="#bfdbfe" />
                        <path d="M2.5 3.5 L 14 13" stroke="#facc15" strokeWidth="1.4" strokeLinecap="round" />
                        <circle cx="11" cy="5" r="2.2" fill="#ef4444" />
                        <circle cx="11" cy="5" r="0.8" fill="white" />
                      </svg>
                      Map
                    </button>
                  </div>

                  {/* Sort — hide for one-way */}
                  {activeTab !== 'oneway' && (
                    <select
                      value={sortBy}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSortBy(val);
                        if (val === 'nearest' && !userLocation) {
                          getUserLocation();
                        }
                      }}
                      className="px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
                    >
                      <option value="default">Sort</option>
                      <option value="nearest">Nearest</option>
                      <option value="price-low">Price ↑</option>
                      <option value="price-high">Price ↓</option>
                      <option value="newest">Newest</option>
                      <option value="name-asc">A → Z</option>
                      <option value="name-desc">Z → A</option>
                    </select>
                  )}
                </div>
              </div>

              {/* Active filter chips row — show chip for every applied filter */}
              {(selectedVehicleType !== 'all' || searchTerm || filterFuelType !== 'all' || filterTransmission !== 'all' || filterSeats !== 'all' || priceActive || filterFeatures.length > 0 || buyActiveCount > 0 || (activeTab === 'oneway' && onewayFrom) || (activeTab === 'outstation' && outstationFrom)) && (
                <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
                  {isBuyTab && BUY_FILTERS.map((group) => {
                    const value = buyFilters[group.key];
                    if (!value || value === 'all') return null;
                    const opt = group.options.find((o) => o.value === value);
                    return (
                      <span key={group.key} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                        {opt?.short}
                        <button onClick={() => setBuyFilter(group.key, 'all')} aria-label={`Remove ${group.label} filter`} className="hover:bg-blue-200 rounded-full p-0.5 -mr-0.5"><X className="w-2.5 h-2.5" /></button>
                      </span>
                    );
                  })}
                  {selectedVehicleType !== 'all' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                      {VEHICLE_TYPES.find(v => v.value === selectedVehicleType)?.label}
                      <button onClick={() => setSelectedVehicleType('all')} className="hover:bg-purple-200 rounded-full p-0.5 -mr-0.5"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  )}
                  {filterFuelType !== 'all' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-medium capitalize">
                      Fuel: {filterFuelType}
                      <button onClick={() => setFilterFuelType('all')} className="hover:bg-orange-200 rounded-full p-0.5 -mr-0.5"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  )}
                  {filterTransmission !== 'all' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full font-medium capitalize">
                      {filterTransmission}
                      <button onClick={() => setFilterTransmission('all')} className="hover:bg-indigo-200 rounded-full p-0.5 -mr-0.5"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  )}
                  {filterSeats !== 'all' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-pink-100 text-pink-700 text-xs rounded-full font-medium">
                      {filterSeats} seats
                      <button onClick={() => setFilterSeats('all')} className="hover:bg-pink-200 rounded-full p-0.5 -mr-0.5"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  )}
                  {priceActive && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                      Price: {priceLabel}
                      <button onClick={() => setPriceRange(null)} className="hover:bg-amber-200 rounded-full p-0.5 -mr-0.5"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  )}
                  {filterFeatures.map((f) => (
                    <span key={f} className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full font-medium capitalize">
                      {f}
                      <button onClick={() => setFilterFeatures(prev => prev.filter(x => x !== f))} className="hover:bg-teal-200 rounded-full p-0.5 -mr-0.5"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  ))}
                  {searchTerm && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full font-medium">
                      "{searchTerm}"
                      <button onClick={() => setSearchTerm('')} className="hover:bg-gray-300 rounded-full p-0.5 -mr-0.5"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  )}
                  {activeTab === 'oneway' && onewayFrom && (
                    <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 text-xs rounded-full font-medium capitalize">
                      {onewayFrom} → {onewayTo}
                    </span>
                  )}
                  {activeTab === 'outstation' && outstationFrom && (
                    <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full font-medium capitalize">
                      {outstationFrom} → {outstationTo}
                    </span>
                  )}
                  <button
                    onClick={() => {
                      setActiveTab(defaultTab);
                      setSelectedVehicleType('all');
                      setSearchTerm('');
                      setFilterFuelType('all');
                      setFilterTransmission('all');
                      setFilterSeats('all');
                      setPriceRange(null);
                      setFilterFeatures([]);
                      setOnewayFrom('');
                      setOnewayTo('');
                      setOnewayDate('');
                      setOnewaySearched(false);
                      setOnewayTrips([]);
                      setOutstationFrom('');
                      setOutstationTo('');
                      setOutstationDate('');
                      setOutstationSearched(false);
                    }}
                    className="flex items-center gap-1 px-2 py-0.5 bg-red-100 hover:bg-red-200 text-red-600 text-xs rounded-full font-medium transition-colors"
                  >
                    <X className="w-3 h-3" />
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* Content below sticky bar */}
            <div className="px-2 py-1 md:px-4 md:py-3">
            {/* Location Status — only show on errors (success state is implicit) */}
            {viewMode === 'list' && sortBy === 'nearest' && locationError && (
              <div className="mb-4 px-3 py-2 rounded-lg text-xs flex items-center gap-2 bg-red-50 text-red-600">
                <Locate className="h-3.5 w-3.5" />
                {locationError}
              </div>
            )}

            {/* Map View */}
            {viewMode === 'map' && (
            <div>
              <div className="relative w-full overflow-hidden rounded-none md:rounded-xl border-0 md:border md:border-gray-200 h-[calc(100vh-200px)] md:h-[500px]">
                {(() => {
                  const nearbyCars = radiusFilteredCars;
                  const otherCars = sortedCars.filter(car =>
                    car.distance === null || car.distance > searchRadius
                  );

                  return (
                    <>
                      <NearbyCarsMap
                        key={`map-${selectedVehicleType}`}
                        cars={nearbyCars}
                        userLocation={userLocation ? { ...userLocation, city: detectedCity } : null}
                        onCarSelect={(car) => {}}
                        onViewDetails={handleViewDetails}
                        onBookNow={handleBookNow}
                        height="100%"
                        className=""
                        centerOnUser={true}
                        searchRadius={searchRadius}
                        selectedVehicleType={selectedVehicleType}
                      />

                      {nearbyCars.length === 0 && (
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] bg-white rounded-lg shadow-lg px-4 py-3 text-center">
                          <p className="text-sm text-orange-600 font-medium mb-1">
                            No {selectedVehicleType !== 'all' ? VEHICLE_TYPES.find(v => v.value === selectedVehicleType)?.label?.toLowerCase() + 's' : 'vehicles'} within {searchRadius}km
                          </p>
                          {otherCars.length > 0 && searchRadius < 100 && (
                            <button
                              onClick={() => { setSearchRadius(Math.min(searchRadius * 2, 100)); setRadiusManuallySet(true); }}
                              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Expand to {Math.min(searchRadius * 2, 100)}km
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
            )}

            {/* One-Way Trips Content */}
            {activeTab === 'oneway' && (
              <>
                {loading ? (
                  <div className="py-16">
                    <CarLoader message="Finding one-way trips..." />
                  </div>
                ) : !onewaySearched ? (
                  <div className="text-center py-20 bg-white rounded-xl border border-gray-100">
                    <ArrowRightLeft className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg mb-2">Search for one-way trips</p>
                    <p className="text-gray-400 text-sm">Enter your pickup and drop city to find available trips</p>
                  </div>
                ) : onewayTrips.length === 0 ? (
                  <div className="text-center py-20 bg-white rounded-xl border border-gray-100">
                    <ArrowRightLeft className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg mb-2">No trips found</p>
                    <p className="text-gray-400 text-sm">No one-way trips available from {onewayFrom} to {onewayTo}</p>
                  </div>
                ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {onewayTrips.map((trip) => (
                  <div key={trip._id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow">
                    {/* Car Image */}
                    {trip.carId?.images?.[0] && (
                      <div className="h-40 overflow-hidden">
                        <img src={cldImg(trip.carId.images[0], { w: 400 })} loading="lazy" alt={`${trip.carId.make} ${trip.carId.model}`} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="p-4">
                      {/* Route */}
                      <div className="flex items-center gap-2 mb-3">
                        <div className="flex-1">
                          <p className="text-xs text-gray-400">From</p>
                          <p className="font-semibold text-gray-900 text-sm">{titleCase(trip.fromCity)}</p>
                        </div>
                        <div className="flex items-center gap-1 text-blue-500">
                          <div className="w-8 h-[2px] bg-blue-300"></div>
                          <ArrowRightLeft className="w-4 h-4" />
                          <div className="w-8 h-[2px] bg-blue-300"></div>
                        </div>
                        <div className="flex-1 text-right">
                          <p className="text-xs text-gray-400">To</p>
                          <p className="font-semibold text-gray-900 text-sm">{titleCase(trip.toCity)}</p>
                        </div>
                      </div>

                      {/* Vehicle Info */}
                      {trip.carId && (
                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                          <Car className="w-3.5 h-3.5" />
                          <span>{formatVehicleName(trip.carId)}</span>
                          {trip.carId.fuelType && <span>• {trip.carId.fuelType}</span>}
                          {trip.carId.seats && <span>• {trip.carId.seats} seats</span>}
                        </div>
                      )}

                      {/* Details Row */}
                      <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(trip.departureDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </span>
                        {trip.departureTime && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {trip.departureTime}
                          </span>
                        )}
                        {trip.estimatedDistance && (
                          <span>{trip.estimatedDistance} km</span>
                        )}
                      </div>

                      {/* Price & Seats */}
                      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                        <div>
                          <span className="text-xl font-bold text-blue-600">₹{trip.price?.toLocaleString('en-IN')}</span>
                          <span className="text-xs text-gray-400 ml-1">per person</span>
                        </div>
                        <span className="flex items-center gap-1 text-xs text-gray-500">
                          <Users className="w-3.5 h-3.5" />
                          {trip.seatsAvailable} seats
                        </span>
                      </div>

                      {/* Owner Info */}
                      {trip.ownerId?.travelsInfo?.name && (
                        <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {trip.ownerId.travelsInfo.name}
                        </p>
                      )}

                      {/* Description */}
                      {trip.description && (
                        <p className="text-xs text-gray-500 mt-2 line-clamp-2">{trip.description}</p>
                      )}

                      {/* Book Now */}
                      <button
                        onClick={() => handleBookOnewayTrip(trip)}
                        className="mt-3 w-full inline-flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow transition-all"
                      >
                        Book Now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
                )}
              </>
            )}

            {/* Content: Loading / Empty / Grid (list view) */}
            {activeTab !== 'oneway' && viewMode === 'list' && (loading ? (
              <div className="py-16">
                <CarLoader message="Finding available vehicles..." />
              </div>
            ) : radiusFilteredCars.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-xl border border-gray-100">
                <Car className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg mb-2">No vehicles found</p>
                <p className="text-gray-400 text-sm mb-4">Try adjusting your filters or search radius</p>
                {isBuyTab && buyActiveCount > 0 && (
                  <button
                    onClick={clearBuyFilters}
                    className="mb-3 px-5 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm font-semibold rounded-lg transition-colors"
                  >
                    Clear {buyActiveCount} used-vehicle {buyActiveCount === 1 ? 'filter' : 'filters'}
                  </button>
                )}
                <button
                  onClick={() => { setActiveTab(defaultTab); setSelectedVehicleType('all'); setSearchTerm(''); clearBuyFilters(); }}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            ) : (
              <>
              {/* Car Cards Grid — 3 col desktop, 2 tablet, 1 mobile */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {paginatedCars.map((car) => {
                  const travels = getTravelsInfo(car);
                  const price = getPrice(car);
                  const isSaleTab = activeTab === 'sale';
                  const isCarForSale = car.forSale === true;

                  return (
                    <div
                      key={car._id}
                      onClick={() => handleViewDetails(car)}
                      className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden border border-gray-100 cursor-pointer group"
                    >
                      {/* Card Image */}
                      <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 relative overflow-hidden">
                        {car.images && car.images.filter(img => img).length > 0 ? (
                          <img
                            src={cldImg(car.images.filter(img => img)[0], { w: 500 })}
                            loading="lazy"
                            alt={`${formatName(car.make)} ${formatName(car.model)}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <Car className="h-16 w-16 text-gray-300" />
                          </div>
                        )}

                        {/* Minimal Badges */}
                        <div className="absolute top-2.5 left-2.5 flex flex-wrap gap-1.5">
                          {isCarForSale && (
                            <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full font-bold shadow">
                              FOR SALE
                            </span>
                          )}
                        </div>

                        {/* Distance pill on image */}
                        {car.distance !== null && car.distance !== undefined && userLocation && (
                          <div className="absolute top-2.5 right-2.5">
                            <span className={`inline-flex items-center text-xs font-semibold px-2 py-1 rounded-full shadow ${
                              car.distance < 2
                                ? 'bg-green-500 text-white'
                                : car.distance < 5
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-white/90 text-gray-700'
                            }`}>
                              <Navigation className="w-3 h-3 mr-1" />
                              {car.distance < 1
                                ? `${Math.round(car.distance * 1000)}m`
                                : `${car.distance}km`
                              }
                            </span>
                          </div>
                        )}

                        {/* Travels Badge */}
                        {travels && (
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 py-2">
                            <div className="flex items-center gap-2">
                              {travels.logo?.url ? (
                                <img src={travels.logo.url} alt={travels.name} className="h-7 w-7 rounded-full object-cover border border-white/50 flex-shrink-0" />
                              ) : (
                                <div className="h-7 w-7 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                                  <Building2 className="h-3.5 w-3.5 text-white" />
                                </div>
                              )}
                              <p className="text-sm font-medium text-white truncate">{formatName(travels.name)}</p>
                              {travels.phone && (
                                <a
                                  href={`tel:${travels.phone}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="hidden sm:flex ml-auto p-1.5 bg-green-500 hover:bg-green-600 rounded-full flex-shrink-0 transition-colors"
                                >
                                  <Phone className="h-3 w-3 text-white" />
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Card Body */}
                      <div className="p-4">
                        {/* Name + Price row */}
                        <div className="flex items-start justify-between mb-2">
                          <div className="min-w-0 flex-1 mr-3">
                            <h4 className="text-base font-bold text-gray-900 truncate">
                              {displayYear(car)} {formatName(car.make)} {formatName(car.model)}
                            </h4>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <span className="text-lg font-bold text-blue-600">{formatCurrency(price.amount)}</span>
                            <span className="text-xs text-gray-400">{price.suffix}</span>
                            {isSaleTab && isCarForSale && car.salePriceType && (
                              <p className={`text-xs font-medium ${getSalePriceTypeTextColor(car.salePriceType)}`}>
                                {getSalePriceTypeLabel(car.salePriceType)}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Subtitle */}
                        <p className="text-xs text-gray-500 mb-3">
                          {formatName(car.fuelType)} &middot; {formatName(car.transmission)} &middot; {car.seats > 1 ? `${car.seats - 1}+1` : car.seats} Seater
                        </p>

                        {/* Specs pills */}
                        <div className="flex flex-wrap gap-1.5">
                          <span className="inline-flex items-center text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full">
                            <VehicleIcon type={car.vehicleType || 'car'} size="sm" />
                            <span className="ml-1">{VEHICLE_TYPES.find(v => v.value === (car.vehicleType || 'car'))?.label}</span>
                          </span>
                          {car.location?.city && (
                            <span className="inline-flex items-center text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full">
                              <MapPin className="h-3 w-3 mr-1 text-gray-400" />
                              {formatName(car.location.city)}
                            </span>
                          )}
                          {car.pricePerKm > 0 && !isSaleTab && (
                            <span className="inline-flex items-center text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full">
                              {formatCurrency(car.pricePerKm)}/km
                            </span>
                          )}
                          {/* Service type pills */}
                          {car.serviceTypes && car.serviceTypes.length > 0 && car.serviceTypes.slice(0, 2).map((type, idx) => (
                            <span key={idx} className={`inline-flex items-center text-xs px-2 py-1 rounded-full font-medium text-white ${getServiceBadgeColor(type)}`}>
                              {getServiceLabel(type)}
                            </span>
                          ))}
                          {car.serviceTypes && car.serviceTypes.length > 2 && (
                            <span className="inline-flex items-center text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full">
                              +{car.serviceTypes.length - 2} more
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <p className="text-sm text-gray-500">
                    Showing <span className="font-medium text-gray-700">{startIndex + 1}-{Math.min(endIndex, radiusFilteredCars.length)}</span> of <span className="font-medium text-gray-700">{radiusFilteredCars.length}</span> Vehicles
                  </p>

                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Prev
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        if (totalPages <= 5) return true;
                        if (page === 1 || page === totalPages) return true;
                        return page >= currentPage - 1 && page <= currentPage + 1;
                      })
                      .reduce((acc, page, idx, arr) => {
                        if (idx > 0 && page - arr[idx - 1] > 1) acc.push('...');
                        acc.push(page);
                        return acc;
                      }, [])
                      .map((item, idx) =>
                        item === '...' ? (
                          <span key={`dots-${idx}`} className="px-2 text-sm text-gray-400">...</span>
                        ) : (
                          <button
                            key={item}
                            onClick={() => setCurrentPage(item)}
                            className={`w-9 h-9 text-sm font-medium rounded-lg transition-colors ${
                              currentPage === item
                                ? 'bg-blue-600 text-white shadow-sm'
                                : 'text-gray-600 bg-white border border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {item}
                          </button>
                        )
                      )}

                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
              </>
            ))}
            </div>
              </>
            )}
          </main>
        </div>
      </div>
      </div>

      {/* Footer — hidden on Browse (app-style viewport-locked page) */}
      <footer className="hidden relative z-10 bg-gray-900 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="col-span-2 sm:col-span-1">
              <Link to={authUser ? '/dashboard' : '/home'} className="flex items-center gap-2 mb-4 hover:opacity-90 transition-opacity">
                <img src={appConfig.logo} alt={appConfig.name} className="h-9 w-9 object-contain rounded-lg" />
                <span className="text-lg font-bold text-white">{appConfig.name}</span>
              </Link>
              <p className="text-sm text-gray-400 mb-4">{appConfig.description}</p>
              <div className="flex items-center gap-3">
                <a href={appConfig.social.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
                <a href={appConfig.social.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                </a>
                <a href={appConfig.social.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </a>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Services</h4>
              <ul className="space-y-2.5">
                <li><button onClick={() => setActiveTab('rental')} className="text-sm text-gray-400 hover:text-white transition-colors">Multi-Day Rental</button></li>
                <li><button onClick={() => setActiveTab('daily')} className="text-sm text-gray-400 hover:text-white transition-colors">Daily Rental</button></li>
                <li><button onClick={() => setActiveTab('outstation')} className="text-sm text-gray-400 hover:text-white transition-colors">Outstation</button></li>
                <li><button onClick={() => setActiveTab('oneway')} className="text-sm text-gray-400 hover:text-white transition-colors">One-Way Trips</button></li>
                <li><button onClick={() => setActiveTab('sale')} className="text-sm text-gray-400 hover:text-white transition-colors">Buy Vehicle</button></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2.5">
                <li><Link to="/about" className="text-sm text-gray-400 hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/support" className="text-sm text-gray-400 hover:text-white transition-colors">Contact Us</Link></li>
                <li><Link to="/support" className="text-sm text-gray-400 hover:text-white transition-colors">Support</Link></li>
                <li><button onClick={() => navigate('/register')} className="text-sm text-gray-400 hover:text-white transition-colors">For Business</button></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2.5">
                <li><Link to="/terms" className="text-sm text-gray-400 hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/privacy" className="text-sm text-gray-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/refund-policy" className="text-sm text-gray-400 hover:text-white transition-colors">Refund Policy</Link></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-500">{appConfig.copyright}</p>
            <div className="flex items-center gap-4">
              <Link to="/terms" className="text-xs text-gray-500 hover:text-gray-300 transition-colors">Terms</Link>
              <Link to="/privacy" className="text-xs text-gray-500 hover:text-gray-300 transition-colors">Privacy</Link>
              <Link to="/refund-policy" className="text-xs text-gray-500 hover:text-gray-300 transition-colors">Refunds</Link>
              <Link to="/support" className="text-xs text-gray-500 hover:text-gray-300 transition-colors">Support</Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Car Details Modal */}
      {detailsModal && selectedCar && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4 mt-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
            {/* Close Button - Fixed at top right */}
            <button
              onClick={() => setDetailsModal(false)}
              className="absolute top-3 right-3 z-20 p-2 bg-white hover:bg-gray-100 rounded-full shadow-lg border border-gray-200 transition-colors"
            >
              <X className="h-6 w-6 text-gray-700" />
            </button>

            {/* Image Gallery */}
            <div className="relative bg-gray-100">
              {selectedCar.images && selectedCar.images.filter(img => img).length > 0 ? (
                <>
                  <img
                    src={cldImg(selectedCar.images.filter(img => img)[selectedImageIndex], { w: 900 })}
                    alt={formatVehicleName(selectedCar, { showYear: false })}
                    className="w-full h-72 sm:h-96 object-cover"
                  />
                  {/* Image Navigation */}
                  {selectedCar.images.filter(img => img).length > 1 && (
                    <>
                      <button
                        onClick={() => setSelectedImageIndex(prev =>
                          prev === 0 ? selectedCar.images.filter(img => img).length - 1 : prev - 1
                        )}
                        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => setSelectedImageIndex(prev =>
                          prev === selectedCar.images.filter(img => img).length - 1 ? 0 : prev + 1
                        )}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 hover:bg-white rounded-full shadow"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                      {/* Image Counter */}
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                        {selectedImageIndex + 1} / {selectedCar.images.filter(img => img).length}
                      </div>
                    </>
                  )}
                  {/* Thumbnail Strip */}
                  {selectedCar.images.filter(img => img).length > 1 && (
                    <div className="flex gap-2 p-3 overflow-x-auto bg-gray-50">
                      {selectedCar.images.filter(img => img).map((img, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedImageIndex(idx)}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                            selectedImageIndex === idx ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent'
                          }`}
                        >
                          <img src={cldImg(img, { w: 120 })} loading="lazy" alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-72 sm:h-96 flex items-center justify-center bg-gray-200">
                  <Car className="h-20 w-20 text-gray-400" />
                </div>
              )}

              {/* Badges on Image */}
              <div className="absolute top-3 left-3 flex flex-wrap gap-2">
                {selectedCar.forSale && (
                  <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold shadow">
                    🏷️ FOR SALE
                  </span>
                )}
                {selectedCar.serviceTypes?.map((type, idx) => (
                  <span key={idx} className={`text-white px-3 py-1 rounded-full text-sm font-medium shadow ${getServiceBadgeColor(type)}`}>
                    {getServiceLabel(type)}
                  </span>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="p-5">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-2xl"><VehicleIcon type={selectedCar.vehicleType || 'car'} size="lg" /></span>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {formatName(selectedCar.make)} {formatName(selectedCar.model)}
                    </h2>
                  </div>
                  <p className="text-gray-500">{selectedCar.year} • {selectedCar.registrationNumber}</p>
                </div>
                <div className="text-right">
                  {selectedCar.forSale && activeTab === 'sale' ? (
                    <>
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedCar.salePrice)}</p>
                      {selectedCar.salePriceType && (
                        <p className={`text-sm font-medium ${getSalePriceTypeTextColor(selectedCar.salePriceType)}`}>
                          {getSalePriceTypeLabel(selectedCar.salePriceType)}
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-bold text-blue-600">{formatCurrency(selectedCar.pricePerDay)}<span className="text-sm font-normal text-gray-500">/day</span></p>
                      {selectedCar.pricePerKm > 0 && (
                        <p className="text-sm text-gray-500">{formatCurrency(selectedCar.pricePerKm)}/km</p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Resale Details — shown when buying */}
              {selectedCar.forSale && activeTab === 'sale' && selectedCar.saleDetails && (
                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <p className="text-sm font-semibold text-gray-800 mb-3">Vehicle Details</p>

                  {selectedCar.saleDetails.loanStatus === 'active' && (
                    <div className="flex items-start gap-2 mb-3 rounded-lg border border-red-200 bg-red-50 p-3">
                      <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                      <p className="text-xs text-red-800 leading-relaxed">
                        <span className="font-semibold">Loan is still running on this vehicle.</span>{' '}
                        Ownership cannot be transferred until the bank closes the loan and issues an NOC.
                      </p>
                    </div>
                  )}

                  {/* A commercial plate follows the vehicle, not the seller — say so
                      before the buyer finds out at the RTO. */}
                  {isCommercialBoard(selectedCar.registrationBoard) && (
                    <div className="flex items-start gap-2 mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <p className="text-xs text-amber-800 leading-relaxed">
                        <span className="font-semibold">{BOARD_LABELS[selectedCar.registrationBoard]} vehicle.</span>{' '}
                        Transfer requires a valid permit and fitness certificate, and it must be insured at commercial rates.
                        Confirm the paperwork with the seller before you pay.
                      </p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3 text-sm">
                    {selectedCar.variant && (
                      <div><p className="text-xs text-gray-500">Variant</p><p className="font-medium text-gray-900">{selectedCar.variant}</p></div>
                    )}
                    {selectedCar.saleDetails.kmDriven != null && (
                      <div><p className="text-xs text-gray-500">KM Driven</p><p className="font-medium text-gray-900">{Number(selectedCar.saleDetails.kmDriven).toLocaleString('en-IN')} km</p></div>
                    )}
                    {selectedCar.saleDetails.noOfOwners && (
                      <div><p className="text-xs text-gray-500">Ownership</p><p className="font-medium text-gray-900">{ownerLabel(selectedCar.saleDetails.noOfOwners)}</p></div>
                    )}
                    {selectedCar.saleDetails.condition && (
                      <div><p className="text-xs text-gray-500">Condition</p><p className="font-medium text-gray-900 capitalize">{selectedCar.saleDetails.condition.replace('-', ' ')}</p></div>
                    )}
                    {selectedCar.saleDetails.registrationYear && (
                      <div><p className="text-xs text-gray-500">Reg. Year</p><p className="font-medium text-gray-900">{selectedCar.saleDetails.registrationYear}</p></div>
                    )}
                    {selectedCar.saleDetails.insuranceValidTill && (() => {
                      const t = expiryTone(selectedCar.saleDetails.insuranceValidTill);
                      return (
                        <div><p className="text-xs text-gray-500">Insurance Till</p>
                          <p className={`font-medium ${t.cls}`}>{fmtMonthYear(selectedCar.saleDetails.insuranceValidTill)}{t.note && ` · ${t.note}`}</p>
                        </div>
                      );
                    })()}
                    <div><p className="text-xs text-gray-500">Accident-free</p><p className={`font-medium ${selectedCar.saleDetails.accidentFree !== false ? 'text-green-600' : 'text-red-600'}`}>{selectedCar.saleDetails.accidentFree !== false ? 'Yes' : 'No'}</p></div>
                    <div><p className="text-xs text-gray-500">RC</p><p className={`font-medium ${selectedCar.saleDetails.rcAvailable !== false ? 'text-green-600' : 'text-gray-500'}`}>{selectedCar.saleDetails.rcAvailable !== false ? 'Available' : 'Not available'}</p></div>

                    <div><p className="text-xs text-gray-500">Loan</p>
                      <p className={`font-medium ${LOAN_STATUS[selectedCar.saleDetails.loanStatus]?.cls || 'text-gray-500'}`}>
                        {LOAN_STATUS[selectedCar.saleDetails.loanStatus]?.text || 'Not stated'}
                      </p>
                    </div>

                    {selectedCar.registrationBoard && (
                      <div><p className="text-xs text-gray-500">Registration</p>
                        <p className={`font-medium ${isCommercialBoard(selectedCar.registrationBoard) ? 'text-amber-600' : 'text-gray-900'}`}>
                          {BOARD_LABELS[selectedCar.registrationBoard] || selectedCar.registrationBoard}
                        </p>
                      </div>
                    )}

                    {isCommercialBoard(selectedCar.registrationBoard) && selectedCar.commercialDetails && (
                      <>
                        {selectedCar.commercialDetails.permitType && (
                          <div><p className="text-xs text-gray-500">Permit</p>
                            <p className="font-medium text-gray-900">{PERMIT_LABELS[selectedCar.commercialDetails.permitType] || selectedCar.commercialDetails.permitType}</p>
                          </div>
                        )}
                        {selectedCar.commercialDetails.permitValidTill && (() => {
                          const t = expiryTone(selectedCar.commercialDetails.permitValidTill);
                          return (
                            <div><p className="text-xs text-gray-500">Permit Till</p>
                              <p className={`font-medium ${t.cls}`}>{fmtMonthYear(selectedCar.commercialDetails.permitValidTill)}{t.note && ` · ${t.note}`}</p>
                            </div>
                          );
                        })()}
                        {selectedCar.commercialDetails.fitnessValidTill && (() => {
                          const t = expiryTone(selectedCar.commercialDetails.fitnessValidTill);
                          return (
                            <div><p className="text-xs text-gray-500">Fitness Till</p>
                              <p className={`font-medium ${t.cls}`}>{fmtMonthYear(selectedCar.commercialDetails.fitnessValidTill)}{t.note && ` · ${t.note}`}</p>
                            </div>
                          );
                        })()}
                        <div><p className="text-xs text-gray-500">Commercial Insurance</p>
                          <p className={`font-medium ${selectedCar.commercialDetails.commercialInsurance ? 'text-green-600' : 'text-amber-600'}`}>
                            {selectedCar.commercialDetails.commercialInsurance ? 'Yes' : 'Not confirmed'}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  <ListingDisclaimer mode="sale" className="mt-3" />
                </div>
              )}

              {/* Rental view gets the renter-facing disclaimer (the sale block above
                  carries its own), so every listing shows exactly one. */}
              {!(selectedCar.forSale && activeTab === 'sale' && selectedCar.saleDetails) && (
                <ListingDisclaimer mode="rental" className="mb-4" />
              )}

              {/* Travels/Owner Info */}
              {getTravelsInfo(selectedCar) && (
                <div className="bg-blue-50 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-3">
                    {getTravelsInfo(selectedCar).logo?.url ? (
                      <img src={getTravelsInfo(selectedCar).logo.url} alt="" className="h-12 w-12 rounded-full object-cover border-2 border-white shadow flex-shrink-0" />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-blue-200 flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-6 w-6 text-blue-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{formatName(getTravelsInfo(selectedCar).name)}</p>
                      <p className="text-sm text-gray-600">Vehicle Owner</p>
                    </div>
                  </div>
                  {getTravelsInfo(selectedCar).phone && (
                    <a href={`tel:${getTravelsInfo(selectedCar).phone}`} className="flex items-center justify-center gap-2 w-full mt-3 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-semibold">
                      <Phone className="h-4 w-4" />
                      Call Now
                    </a>
                  )}
                </div>
              )}

              {/* Specifications Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <Users className="h-5 w-5 text-blue-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-gray-900">{selectedCar.seats > 1 ? `${selectedCar.seats - 1}+1` : selectedCar.seats}</p>
                  <p className="text-xs text-gray-500">Seater</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <Fuel className="h-5 w-5 text-green-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-gray-900">{formatName(selectedCar.fuelType)}</p>
                  <p className="text-xs text-gray-500">Fuel</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <Settings className="h-5 w-5 text-purple-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-gray-900">{formatName(selectedCar.transmission)}</p>
                  <p className="text-xs text-gray-500">Transmission</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <MapPin className="h-5 w-5 text-red-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-gray-900">{formatName(selectedCar.location?.city) || 'N/A'}</p>
                  <p className="text-xs text-gray-500">Location</p>
                </div>
              </div>

              {/* Additional Details */}
              {(selectedCar.color || selectedCar.mileage) && (
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {selectedCar.color && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="w-4 h-4 rounded-full bg-gray-300 border"></span>
                      Color: <span className="font-medium text-gray-900">{formatName(selectedCar.color)}</span>
                    </div>
                  )}
                  {selectedCar.mileage > 0 && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Gauge className="h-4 w-4" />
                      Mileage: <span className="font-medium text-gray-900">{selectedCar.mileage} km/l</span>
                    </div>
                  )}
                </div>
              )}

              {/* Features */}
              {selectedCar.features && selectedCar.features.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Features</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedCar.features.map((feature, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {selectedCar.description && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Description</h4>
                  <p className="text-sm text-gray-600">{selectedCar.description}</p>
                </div>
              )}

              {/* Vehicle-specific details */}
              {selectedCar.vehicleDetails && (
                <div className="mb-4">
                  {(selectedCar.vehicleType === 'bike' || selectedCar.vehicleType === 'scooter') && selectedCar.vehicleDetails.engineCC > 0 && (
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-gray-600">Engine: <strong>{selectedCar.vehicleDetails.engineCC} CC</strong></span>
                      {selectedCar.vehicleDetails.helmetIncluded && (
                        <span className="text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="h-4 w-4" /> Helmet Included
                        </span>
                      )}
                    </div>
                  )}
                  {(selectedCar.vehicleType === 'bus' || selectedCar.vehicleType === 'tempo-traveller') && (
                    <div className="flex flex-wrap gap-3 text-sm">
                      {selectedCar.vehicleDetails.acAvailable && (
                        <span className="text-blue-600 flex items-center gap-1">
                          <CheckCircle2 className="h-4 w-4" /> AC Available
                        </span>
                      )}
                      {selectedCar.vehicleDetails.sleeper && (
                        <span className="text-purple-600 flex items-center gap-1">
                          <CheckCircle2 className="h-4 w-4" /> Sleeper
                        </span>
                      )}
                      {selectedCar.vehicleDetails.pushback && (
                        <span className="text-indigo-600 flex items-center gap-1">
                          <CheckCircle2 className="h-4 w-4" /> Pushback Seats
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={() => setDetailsModal(false)}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                {selectedCar.forSale && activeTab === 'sale' ? (
                  <button
                    onClick={() => {
                      setDetailsModal(false);
                      handleInquiry(selectedCar);
                    }}
                    className="flex-1 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Inquire to Buy
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      setDetailsModal(false);
                      handleBookNow(selectedCar);
                    }}
                    className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Book Now
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {bookingModal && selectedCar && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[9999] sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-xl w-full sm:max-w-md max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">
                  {bookingStep === 'confirm'
                    ? 'Confirm Booking'
                    : bookingData.rentalType === 'sale' ? 'Buy This Vehicle' : 'Book This Vehicle'
                  }
                </h3>
                <button
                  onClick={() => setBookingModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Selected Car Info */}
              <div className="bg-gray-50 rounded-xl p-3 mb-4">
                {selectedCar.images?.[0] && (
                  <div className="h-32 sm:h-36 rounded-lg overflow-hidden mb-3">
                    <img src={cldImg(selectedCar.images[0], { w: 300 })} loading="lazy" alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-bold text-gray-900 text-base">{formatName(selectedCar.make)} {formatName(selectedCar.model)}</h4>
                    <div className="flex items-center gap-1.5 mt-1">
                      <span className="inline-flex items-center px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded">
                        {VEHICLE_TYPES.find(v => v.value === (selectedCar.vehicleType || 'car'))?.label || 'Car'}
                      </span>
                      <span className="text-xs text-gray-500">{selectedCar.year} • {formatName(selectedCar.fuelType)} • {selectedCar.seats > 1 ? `${selectedCar.seats - 1}+1` : selectedCar.seats} Seater</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* OTP Verification Step */}
              {otpStep !== 'verified' && (
                <div className="space-y-4">
                  {otpStep === 'phone' && (
                    <>
                      <div className="text-center mb-2">
                        <p className="text-sm text-gray-600">Verify yourself to book — choose how you'd like to receive the OTP</p>
                      </div>

                      {/* Verification method — WhatsApp (phone) or Email */}
                      <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-lg">
                        <button
                          type="button"
                          onClick={() => { setOtpMethod('phone'); setOtpError(''); }}
                          className={`flex items-center justify-center gap-1.5 py-2 rounded-md text-sm font-medium transition-colors ${otpMethod === 'phone' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                          <Phone className="h-4 w-4" /> WhatsApp
                        </button>
                        <button
                          type="button"
                          onClick={() => { setOtpMethod('email'); setOtpError(''); }}
                          className={`flex items-center justify-center gap-1.5 py-2 rounded-md text-sm font-medium transition-colors ${otpMethod === 'email' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                          <Mail className="h-4 w-4" /> Email
                        </button>
                      </div>

                      {/* Phone Number — always collected (owner contact + WhatsApp OTP target) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm font-medium">+91</span>
                          <input
                            type="tel"
                            placeholder="Enter 10-digit mobile number"
                            value={bookingData.phone}
                            onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white text-lg tracking-wider"
                            maxLength={10}
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {otpMethod === 'phone'
                            ? "We'll send your OTP to this number on WhatsApp"
                            : 'Owner will contact you on this number'}
                        </p>
                      </div>

                      {/* Email — only when verifying via email */}
                      {otpMethod === 'email' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address <span className="text-red-500">*</span> <span className="text-gray-400 font-normal">(for verification)</span>
                        </label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="email"
                            placeholder="Enter your email for OTP"
                            value={otpEmail}
                            onChange={(e) => setOtpEmail(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                          />
                        </div>
                      </div>
                      )}

                      {/* No email? Contact owner directly */}
                      {selectedCar?.ownerId?.travelsInfo?.phone && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <p className="text-xs text-amber-800 font-medium mb-1">Don't have an email?</p>
                          <p className="text-xs text-amber-700">
                            Call the vehicle owner directly to book:
                          </p>
                          <a
                            href={`tel:${selectedCar.ownerId.travelsInfo.phone}`}
                            className="inline-flex items-center gap-1.5 mt-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-lg transition-colors"
                          >
                            <Phone className="h-3 w-3" />
                            Call {selectedCar.ownerId.travelsInfo.phone}
                          </a>
                        </div>
                      )}

                      {otpError && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                          <X className="h-4 w-4" /> {otpError}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={otpLoading || (otpMethod === 'phone' ? bookingData.phone.replace(/\D/g, '').length < 10 : !otpEmail || !otpEmail.includes('@'))}
                        className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        {otpLoading ? (
                          <><Loader2 className="h-5 w-5 animate-spin" /> Sending OTP...</>
                        ) : (
                          'Send OTP'
                        )}
                      </button>
                    </>
                  )}

                  {otpStep === 'otp' && (
                    <>
                      <div className="text-center mb-2">
                        <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <CheckCircle className="h-7 w-7 text-green-600" />
                        </div>
                        <p className="text-sm text-gray-600">
                          OTP sent to <span className="font-semibold text-gray-900">
                            {otpMethod === 'phone' ? `+91 ${bookingData.phone}` : otpEmail}
                          </span>
                        </p>
                        <button
                          type="button"
                          onClick={() => { setOtpStep('phone'); setOtp(['', '', '', '', '', '']); setOtpError(''); }}
                          className="text-xs text-blue-600 hover:underline mt-1"
                        >
                          Change {otpMethod === 'phone' ? 'number' : 'email'}
                        </button>
                      </div>


                      {/* OTP Input Boxes */}
                      <div className="flex justify-center gap-3">
                        {otp.map((digit, idx) => (
                          <input
                            key={idx}
                            id={`otp-input-${idx}`}
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={digit}
                            onChange={(e) => handleOtpChange(idx, e.target.value)}
                            onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                            className="w-11 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                            autoFocus={idx === 0}
                          />
                        ))}
                      </div>

                      {otpError && (
                        <p className="text-sm text-red-600 text-center flex items-center justify-center gap-1">
                          <X className="h-4 w-4" /> {otpError}
                        </p>
                      )}

                      <button
                        type="button"
                        onClick={handleVerifyOtp}
                        disabled={otpLoading || otp.join('').length < 6}
                        className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        {otpLoading ? (
                          <><Loader2 className="h-5 w-5 animate-spin" /> Verifying...</>
                        ) : (
                          <><CheckCircle2 className="h-5 w-5" /> Verify OTP</>
                        )}
                      </button>

                      {/* Resend Timer */}
                      <div className="text-center">
                        {otpTimer > 0 ? (
                          <p className="text-sm text-gray-500">Resend OTP in <span className="font-semibold text-gray-700">{otpTimer}s</span></p>
                        ) : (
                          <button
                            type="button"
                            onClick={handleResendOtp}
                            disabled={otpLoading}
                            className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline"
                          >
                            Resend OTP
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Booking Form - Only shown after OTP verification */}
              {otpStep === 'verified' && bookingStep === 'form' && (
              <form onSubmit={handleBookingSubmit} className="space-y-4">
                {/* Verified Badge */}
                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-green-800">
                    {otpMethod === 'email'
                      ? <>Email verified: <strong>{otpEmail}</strong></>
                      : <>Phone verified: <strong>+91 {bookingData.phone}</strong></>}
                  </span>
                  {verifiedCustomer?.isNew && (
                    <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">New Customer</span>
                  )}
                </div>

                {/* Service Type Selection - Only show if car has multiple services */}
                {bookingData.rentalType !== 'sale' && selectedCar.serviceTypes && selectedCar.serviceTypes.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Booking Type <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedCar.serviceTypes.map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setBookingData({ ...bookingData, rentalType: type })}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            bookingData.rentalType === type
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            {type === 'daily' && <Clock className="h-4 w-4 text-purple-500" />}
                            {type === 'rental' && <Calendar className="h-4 w-4 text-green-500" />}
                            {type === 'km-based' && <Gauge className="h-4 w-4 text-orange-500" />}
                            {type === 'outstation' && <Map className="h-4 w-4 text-teal-500" />}
                            {type === 'local' && <Navigation className="h-4 w-4 text-indigo-500" />}
                            <span className="text-sm font-medium text-gray-900">{getServiceLabel(type)}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {type === 'daily' && `${formatCurrency(selectedCar.pricePerDay)}/day`}
                            {type === 'rental' && `${formatCurrency(selectedCar.pricePerDay)}/day`}
                            {type === 'km-based' && `${formatCurrency(selectedCar.pricePerKm)}/km`}
                            {type === 'outstation' && `${formatCurrency(selectedCar.outstationPricePerKm || selectedCar.pricePerKm)}/km`}
                            {type === 'local' && `${formatCurrency(selectedCar.pricePerHour || selectedCar.pricePerDay / 8)}/hr`}
                          </p>
                        </button>
                      ))}
                      {selectedCar.forSale && (
                        <button
                          type="button"
                          onClick={() => setBookingData({ ...bookingData, rentalType: 'sale' })}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            bookingData.rentalType === 'sale'
                              ? 'border-red-500 bg-red-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <ShoppingCart className="h-4 w-4 text-red-500" />
                            <span className="text-sm font-medium text-gray-900">Buy Vehicle</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{formatCurrency(selectedCar.salePrice)}</p>
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Name - shown after verification */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your full name"
                    value={bookingData.name}
                    onChange={(e) => setBookingData({ ...bookingData, name: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                    required
                  />
                </div>


                {/* Dynamic Fields based on Rental Type */}
                {bookingData.rentalType !== 'sale' && (
                  <>
                    {/* Date Fields - For daily, rental, outstation */}
                    {['daily', 'rental', 'outstation', 'km-based'].includes(bookingData.rentalType) && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Start Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={bookingData.startDate}
                            onChange={(e) => setBookingData({ ...bookingData, startDate: e.target.value })}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white text-sm"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            End Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={bookingData.endDate}
                            onChange={(e) => setBookingData({ ...bookingData, endDate: e.target.value })}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white text-sm"
                            required
                          />
                        </div>
                      </div>
                    )}

                    {/* Local Trip - Date and Hours */}
                    {bookingData.rentalType === 'local' && (
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={bookingData.startDate}
                            onChange={(e) => setBookingData({ ...bookingData, startDate: e.target.value, endDate: e.target.value })}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white text-sm"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Hours <span className="text-red-500">*</span>
                          </label>
                          <select
                            value={bookingData.hours}
                            onChange={(e) => setBookingData({ ...bookingData, hours: e.target.value })}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white text-sm"
                            required
                          >
                            <option value="4">4 Hours</option>
                            <option value="8">8 Hours (Full Day)</option>
                            <option value="12">12 Hours</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Estimated KM - For km-based and outstation */}
                    {['km-based', 'outstation'].includes(bookingData.rentalType) && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Estimated Distance (KM) <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Gauge className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-orange-500" />
                          <input
                            type="number"
                            placeholder="Enter estimated kilometers"
                            value={bookingData.estimatedKm}
                            onChange={(e) => setBookingData({ ...bookingData, estimatedKm: e.target.value })}
                            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white"
                            required
                            min="1"
                          />
                        </div>
                      </div>
                    )}

                    {/* Pickup Location */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pickup Location <span className="text-red-500">*</span>
                      </label>
                      <AddressAutocomplete
                        label=""
                        placeholder={bookingData.rentalType === 'outstation' ? 'From City (e.g., Bangalore)' : 'Enter pickup address'}
                        value={bookingData.pickupLocation}
                        onChange={(loc) => setBookingData({ ...bookingData, pickupLocation: loc.address })}
                        required
                      />
                    </div>

                    {/* Drop-off Location */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Drop-off Location <span className="text-red-500">*</span>
                      </label>
                      <AddressAutocomplete
                        label=""
                        placeholder={bookingData.rentalType === 'outstation' ? 'To City (e.g., Chennai)' : 'Enter drop-off address'}
                        value={bookingData.dropoffLocation}
                        onChange={(loc) => setBookingData({ ...bookingData, dropoffLocation: loc.address })}
                        required
                      />
                    </div>
                  </>
                )}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {bookingData.rentalType === 'sale' ? 'Message' : 'Special Requests'} (Optional)
                  </label>
                  <textarea
                    placeholder={bookingData.rentalType === 'sale' ? 'Any questions about this vehicle...' : 'Any special requests or requirements...'}
                    value={bookingData.notes}
                    onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-gray-900 bg-white text-sm"
                  />
                </div>

                {/* Price Summary */}
                {bookingData.rentalType !== 'sale' && (
                  <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Estimated Price:</span>
                      <span className="text-lg font-bold text-blue-600">
                        {bookingData.rentalType === 'km-based' && bookingData.estimatedKm
                          ? formatCurrency(selectedCar.pricePerKm * parseInt(bookingData.estimatedKm || 0))
                          : bookingData.rentalType === 'outstation' && bookingData.estimatedKm
                          ? formatCurrency((selectedCar.outstationPricePerKm || selectedCar.pricePerKm) * parseInt(bookingData.estimatedKm || 0))
                          : bookingData.rentalType === 'local'
                          ? formatCurrency((selectedCar.pricePerHour || selectedCar.pricePerDay / 8) * parseInt(bookingData.hours || 4))
                          : bookingData.startDate && bookingData.endDate
                          ? formatCurrency(selectedCar.pricePerDay * Math.max(1, Math.ceil((new Date(bookingData.endDate) - new Date(bookingData.startDate)) / (1000 * 60 * 60 * 24))))
                          : formatCurrency(selectedCar.pricePerDay)
                        }
                      </span>
                    </div>
                    <p className="text-xs text-amber-600 mt-2 italic">
                      * Toll, parking, and other charges are not included and may apply based on actual usage
                    </p>
                  </div>
                )}

                {/* Sale Price + your offer */}
                {bookingData.rentalType === 'sale' && (
                  <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Sale Price:</span>
                      <span className="text-lg font-bold text-red-600">
                        {formatCurrency(selectedCar.salePrice)}
                        {selectedCar.salePriceType && (
                          <span className="ml-2 text-xs font-medium text-gray-500">{getSalePriceTypeLabel(selectedCar.salePriceType)}</span>
                        )}
                      </span>
                    </div>
                    {['negotiable', 'best-offer'].includes(selectedCar.salePriceType) && (
                      <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Your Offer (₹) <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <input
                          type="number" min="0"
                          placeholder="Enter the price you'd like to offer"
                          value={bookingData.offerPrice || ''}
                          onChange={(e) => setBookingData({ ...bookingData, offerPrice: e.target.value })}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-gray-900 bg-white text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">The seller will review your offer and get back to you.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Error Message - Above Submit Button */}
                {error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="flex-1 text-sm text-red-700">{error}</p>
                      <button
                        type="button"
                        onClick={() => setError('')}
                        className="flex-shrink-0 p-0.5 rounded hover:bg-red-100 transition-colors"
                      >
                        <X className="w-4 h-4 text-red-400" />
                      </button>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={bookingLoading}
                  className={`w-full py-3 rounded-lg font-medium transition-colors ${
                    bookingData.rentalType === 'sale'
                      ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-400'
                      : 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400'
                  } text-white`}
                >
                  {bookingData.rentalType === 'sale' ? 'Review & Send Inquiry' : 'Review Booking'}
                </button>
              </form>
              )}

              {/* Confirmation Step - Review before final submit */}
              {otpStep === 'verified' && bookingStep === 'confirm' && selectedCar && (
                <div className="space-y-4">
                  {/* Header */}
                  <div className="text-center pb-2 border-b border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900">Review Your Booking</h3>
                    <p className="text-sm text-gray-500 mt-1">Please confirm the details below</p>
                  </div>

                  {/* Car Info */}
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    {selectedCar.images?.[0] ? (
                      <img src={cldImg(selectedCar.images[0], { w: 150 })} loading="lazy" alt={selectedCar.make} className="w-16 h-12 object-cover rounded-lg" />
                    ) : (
                      <div className="w-16 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                        <Car className="h-6 w-6 text-gray-400" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-gray-900">{formatVehicleName(selectedCar, { showYear: false })}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="inline-flex items-center px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-medium rounded">
                          {VEHICLE_TYPES.find(v => v.value === (selectedCar.vehicleType || 'car'))?.label || 'Car'}
                        </span>
                        <span className="text-xs text-gray-500">{selectedCar.registrationNumber} &middot; {selectedCar.fuelType} &middot; {selectedCar.transmission}</span>
                      </div>
                    </div>
                  </div>

                  {/* Booking Details */}
                  <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
                    <div className="flex justify-between px-4 py-2.5">
                      <span className="text-sm text-gray-500">Customer</span>
                      <span className="text-sm font-medium text-gray-900">{bookingData.name}</span>
                    </div>
                    <div className="flex justify-between px-4 py-2.5">
                      <span className="text-sm text-gray-500">Phone</span>
                      <span className="text-sm font-medium text-gray-900">+91 {bookingData.phone}</span>
                    </div>
                    <div className="flex justify-between px-4 py-2.5">
                      <span className="text-sm text-gray-500">Booking Type</span>
                      <span className="text-sm font-medium text-gray-900">{getServiceLabel(bookingData.rentalType)}</span>
                    </div>

                    {bookingData.rentalType !== 'sale' && (
                      <>
                        {['daily', 'rental', 'outstation', 'km-based'].includes(bookingData.rentalType) && (
                          <>
                            <div className="flex justify-between px-4 py-2.5">
                              <span className="text-sm text-gray-500">Start Date</span>
                              <span className="text-sm font-medium text-gray-900">{new Date(bookingData.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            </div>
                            <div className="flex justify-between px-4 py-2.5">
                              <span className="text-sm text-gray-500">End Date</span>
                              <span className="text-sm font-medium text-gray-900">{new Date(bookingData.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            </div>
                          </>
                        )}
                        {bookingData.rentalType === 'local' && (
                          <>
                            <div className="flex justify-between px-4 py-2.5">
                              <span className="text-sm text-gray-500">Date</span>
                              <span className="text-sm font-medium text-gray-900">{new Date(bookingData.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            </div>
                            <div className="flex justify-between px-4 py-2.5">
                              <span className="text-sm text-gray-500">Hours</span>
                              <span className="text-sm font-medium text-gray-900">{bookingData.hours} Hours</span>
                            </div>
                          </>
                        )}
                        {['km-based', 'outstation'].includes(bookingData.rentalType) && (
                          <div className="flex justify-between px-4 py-2.5">
                            <span className="text-sm text-gray-500">Estimated Distance</span>
                            <span className="text-sm font-medium text-gray-900">{bookingData.estimatedKm} km</span>
                          </div>
                        )}
                        <div className="flex justify-between px-4 py-2.5">
                          <span className="text-sm text-gray-500">Pickup</span>
                          <span className="text-sm font-medium text-gray-900 text-right max-w-[60%]">{bookingData.pickupLocation}</span>
                        </div>
                        <div className="flex justify-between px-4 py-2.5">
                          <span className="text-sm text-gray-500">Drop-off</span>
                          <span className="text-sm font-medium text-gray-900 text-right max-w-[60%]">{bookingData.dropoffLocation}</span>
                        </div>
                      </>
                    )}

                    {bookingData.notes && (
                      <div className="flex justify-between px-4 py-2.5">
                        <span className="text-sm text-gray-500">Notes</span>
                        <span className="text-sm text-gray-700 text-right max-w-[60%]">{bookingData.notes}</span>
                      </div>
                    )}
                  </div>

                  {/* Price */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Estimated Amount</span>
                      <span className="text-xl font-bold text-green-700">
                        {bookingData.rentalType === 'sale'
                          ? formatCurrency(selectedCar.salePrice)
                          : bookingData.rentalType === 'km-based' && bookingData.estimatedKm
                          ? formatCurrency(selectedCar.pricePerKm * parseInt(bookingData.estimatedKm || 0))
                          : bookingData.rentalType === 'outstation' && bookingData.estimatedKm
                          ? formatCurrency((selectedCar.outstationPricePerKm || selectedCar.pricePerKm) * parseInt(bookingData.estimatedKm || 0))
                          : bookingData.rentalType === 'local'
                          ? formatCurrency((selectedCar.pricePerHour || selectedCar.pricePerDay / 8) * parseInt(bookingData.hours || 4))
                          : bookingData.startDate && bookingData.endDate
                          ? formatCurrency(selectedCar.pricePerDay * Math.max(1, Math.ceil((new Date(bookingData.endDate) - new Date(bookingData.startDate)) / (1000 * 60 * 60 * 24))))
                          : formatCurrency(selectedCar.pricePerDay)
                        }
                      </span>
                    </div>
                    <p className="text-xs text-amber-600 mt-2 italic">
                      * Toll, parking, and other charges may apply based on actual usage
                    </p>
                  </div>

                  {/* Error */}
                  {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setBookingStep('form')}
                      className="flex-1 py-3 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Edit Details
                    </button>
                    <button
                      type="button"
                      onClick={handleFinalBooking}
                      disabled={bookingLoading}
                      className={`flex-1 py-3 rounded-lg font-medium transition-colors text-white ${
                        bookingData.rentalType === 'sale'
                          ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-400'
                          : 'bg-green-600 hover:bg-green-700 disabled:bg-green-400'
                      }`}
                    >
                      {bookingLoading ? 'Submitting...' : bookingData.rentalType === 'sale' ? 'Confirm & Send Inquiry' : 'Confirm & Book Now'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success Modal - Enhanced */}
      {successModal && bookingResult && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-bounce-in relative">
            {/* Close Button */}
            <button
              onClick={() => { setSuccessModal(false); setBookingResult(null); }}
              className="absolute top-3 right-3 z-20 p-2 bg-white/80 hover:bg-white rounded-full shadow-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-700" />
            </button>
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-8 text-center text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm border-4 border-white/30">
                  <CheckCircle className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-1">
                  {bookingData.rentalType === 'sale' ? 'Inquiry Sent!' : 'Booking Received!'}
                </h3>
                <p className="text-green-100 text-sm">
                  Your request has been submitted successfully
                </p>
              </div>
            </div>

            <div className="p-6">
              {/* Status Badge */}
              <div className="flex justify-center -mt-10 mb-4">
                <div className="bg-yellow-100 border-2 border-yellow-300 rounded-full px-4 py-2 flex items-center space-x-2 shadow-lg">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse"></div>
                  <span className="text-yellow-800 font-semibold text-sm">Awaiting Confirmation</span>
                </div>
              </div>

              {/* What happens next */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-4 border border-blue-100">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                  <Clock className="h-4 w-4 mr-2 text-blue-600" />
                  What happens next?
                </h4>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
                    <p className="text-sm text-gray-600">Owner receives your booking request</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">2</div>
                    <p className="text-sm text-gray-600">They will call you to confirm availability</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">3</div>
                    <p className="text-sm text-gray-600">Once confirmed, your trip is scheduled!</p>
                  </div>
                </div>
              </div>

              {/* Booking Details Card */}
              <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">Booking Details</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center py-1 border-b border-gray-200">
                    <span className="text-gray-500 text-sm">Car</span>
                    <span className="font-semibold text-gray-900">
                      {formatName(bookingResult.booking?.carId?.make)} {formatName(bookingResult.booking?.carId?.model)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-1 border-b border-gray-200">
                    <span className="text-gray-500 text-sm">Your Phone</span>
                    <span className="font-semibold text-gray-900">{bookingResult.customer?.phone}</span>
                  </div>
                  {bookingResult.booking?.totalCost > 0 && (
                    <>
                      <div className="flex justify-between items-center py-1 border-b border-gray-200">
                        <span className="text-gray-500 text-sm">Estimated Amount</span>
                        <span className="font-bold text-green-600 text-lg">
                          {formatCurrency(bookingResult.booking?.totalCost)}
                        </span>
                      </div>
                      <p className="text-xs text-amber-600 italic py-1 border-b border-gray-200">
                        * Toll, parking, and other charges are not included and may apply based on actual usage
                      </p>
                    </>
                  )}
                  {bookingResult.travelsInfo && (
                    <div className="flex justify-between items-center py-1">
                      <span className="text-gray-500 text-sm">Travels Company</span>
                      <div className="flex items-center space-x-2">
                        {bookingResult.travelsInfo.logo?.url ? (
                          <img src={bookingResult.travelsInfo.logo.url} alt="" className="h-6 w-6 rounded-full object-cover" />
                        ) : (
                          <Building2 className="h-5 w-5 text-blue-600" />
                        )}
                        <span className="font-semibold text-gray-900">{bookingResult.travelsInfo.name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* New Account Info */}
              {bookingResult.customer?.isNew && (
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-4 mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-purple-800 text-sm">Account Created!</p>
                      <p className="text-purple-700 text-xs mt-1">
                        Login with your phone number to track all your bookings.
                        <br />
                        <span className="font-medium">Default Password: {bookingResult.customer?.phone}</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Owner Button */}
              {bookingResult.travelsInfo?.phone && (
                <a
                  href={`tel:${bookingResult.travelsInfo.phone}`}
                  className="w-full mb-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-3 rounded-xl font-medium transition-all flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
                >
                  <Phone className="h-5 w-5" />
                  <span>Call Owner Now</span>
                </a>
              )}

              {/* Track Booking Button */}
              <Link
                to="/track-booking"
                className="w-full mb-3 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-3 rounded-xl font-medium transition-all flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl"
              >
                <Search className="h-5 w-5" />
                <span>Track My Booking</span>
              </Link>

              {/* Continue Browsing Button */}
              <button
                onClick={() => {
                  setSuccessModal(false);
                  setBookingResult(null);
                  fetchCars();
                }}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-xl font-medium transition-colors flex items-center justify-center space-x-2"
              >
                <ChevronRight className="h-5 w-5" />
                <span>Continue Browsing</span>
              </button>

              {/* Footer Note */}
              <p className="text-center text-xs text-gray-400 mt-4">
                Track your booking status anytime using your phone number
              </p>
            </div>
          </div>
        </div>
      )}

    </div>

    {/* Mobile Filter — at top level, outside all layout divs */}
    {sidebarOpen && (
      <div className="md:hidden fixed inset-0 z-[9999] bg-white flex flex-col" style={{ height: '100dvh' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0">
          <h3 className="text-lg font-bold text-gray-900">Filters</h3>
          <button onClick={() => setSidebarOpen(false)} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {userLocation && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <Navigation className="h-4 w-4 text-blue-600" />
                  Search Radius
                </h4>
                <span className="text-xs font-semibold text-blue-600">Within {searchRadius} km</span>
              </div>
              {(() => {
                const config = getRadiusConfig(selectedVehicleType);
                const rMin = Math.min(...config.buttons);
                const rMax = Math.max(...config.buttons, ...(config.dropdown || []));
                return (
                  <RadiusSlider
                    min={rMin}
                    max={rMax}
                    value={Math.min(Math.max(searchRadius, rMin), rMax)}
                    onChange={(km) => { setSearchRadius(km); setRadiusManuallySet(true); }}
                  />
                );
              })()}
              <p className="text-xs text-gray-500 mt-1">{radiusFilteredCars.length} found within {searchRadius} km</p>
            </div>
          )}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2"><Car className="h-4 w-4 text-blue-600" />Vehicle Type</h4>
            <select
              value={selectedVehicleType}
              onChange={(e) => setSelectedVehicleType(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-700 bg-white focus:ring-2 focus:ring-blue-500"
            >
              {vehicleTypeOptions.map((type) => (
                <option key={type.value} value={type.value}>
                  {optionGlyph(type.icon) ? optionGlyph(type.icon) + ' ' : ''}{type.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2"><Fuel className="h-4 w-4 text-blue-600" />Fuel Type</h4>
            <div className="flex flex-wrap gap-1.5">
              {['all', 'Petrol', 'Diesel', 'Electric', 'CNG', 'Hybrid'].map((fuel) => (
                <button key={fuel} onClick={() => setFilterFuelType(fuel)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-xl transition-colors ${filterFuelType === fuel ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {fuel === 'all' ? 'All' : fuel}
                </button>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2"><Settings className="h-4 w-4 text-blue-600" />Transmission</h4>
            <div className="flex flex-wrap gap-1.5">
              {['all', 'Manual', 'Automatic'].map((t) => (
                <button key={t} onClick={() => setFilterTransmission(t)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-xl transition-colors ${filterTransmission === t ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {t === 'all' ? 'All' : t}
                </button>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2"><Users className="h-4 w-4 text-blue-600" />Seats</h4>
            <div className="flex flex-wrap gap-1.5">
              {[{ value: 'all', label: 'All' }, { value: '2', label: '1-2' }, { value: '4-5', label: '4-5' }, { value: '7+', label: '7+' }].map((s) => (
                <button key={s.value} onClick={() => setFilterSeats(s.value)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-xl transition-colors ${filterSeats === s.value ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-800">{activeTab === 'sale' ? 'Price' : 'Price / Day'}</h4>
              {priceActive && (
                <button onClick={() => setPriceRange(null)} className="text-xs font-medium text-blue-600">Reset</button>
              )}
            </div>
            <PriceRangeSlider
              min={priceBounds.min}
              max={priceBounds.max}
              step={priceBounds.step}
              value={priceRange || [priceBounds.min, priceBounds.max]}
              onChange={setPriceRange}
              format={(v) => fmtPrice(v, activeTab)}
            />
          </div>
          {isBuyTab && (
            <div className="rounded-2xl border border-gray-100 bg-gray-50/60 p-3">
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-sm font-bold text-gray-900">Used vehicle</h4>
                {buyActiveCount > 0 && (
                  <button onClick={clearBuyFilters} className="text-xs font-semibold text-blue-600">
                    Clear ({buyActiveCount})
                  </button>
                )}
              </div>
              {/* Collapsed by default: six groups open at once would push the
                  sheet's apply button far below the thumb. */}
              {BUY_FILTERS.map((group) => (
                <BuyFilterGroup
                  key={group.key}
                  group={group}
                  value={buyFilters[group.key]}
                  onChange={setBuyFilter}
                      counts={buyOptionCounts}
                  defaultOpen={false}
                />
              ))}
            </div>
          )}
          <div>
            <h4 className="text-sm font-semibold text-gray-800 mb-2">Features</h4>
            <div className="flex flex-wrap gap-1.5">
              {['AC', 'GPS', 'Bluetooth', 'Airbags', 'ABS', 'Sunroof', 'Power Steering', 'Rear Camera', 'Music System', 'Helmet Included'].map((f) => (
                <button key={f} onClick={() => setFilterFeatures(prev => prev.includes(f) ? prev.filter(x => x !== f) : [...prev, f])}
                  className={`px-2.5 py-1.5 text-xs font-medium rounded-full border transition-colors ${filterFeatures.includes(f) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-500 border-gray-200'}`}>
                  {filterFeatures.includes(f) ? '✓ ' : ''}{f}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t px-5 py-4 flex gap-3 flex-shrink-0 bg-white">
          <button onClick={() => { setSearchTerm(''); setSelectedVehicleType('all'); setSortBy('default'); setActiveTab(defaultTab); setFilterFuelType('all'); setFilterTransmission('all'); setFilterSeats('all'); setPriceRange(null); setFilterFeatures([]); setFilterAvailability('all'); setRadiusManuallySet(false); setSidebarOpen(false); }}
            className="flex-1 py-3 text-sm text-red-600 font-semibold rounded-xl border border-red-200 hover:bg-red-50 transition-colors">
            Clear All
          </button>
          <button onClick={() => setSidebarOpen(false)}
            className="flex-1 py-3 text-sm text-white font-semibold rounded-xl bg-blue-600 hover:bg-blue-700 transition-colors">
            Show {radiusFilteredCars.length} Results
          </button>
        </div>
      </div>
    )}

    {/* User Menu Dropdown — at top level */}
    {showUserMenu && authUser && (
      <>
        <div className="fixed inset-0 bg-black/30 z-[9990]" onClick={() => setShowUserMenu(false)} />
        <div className="fixed right-3 top-12 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 z-[9991] overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <p className="text-sm font-semibold text-gray-900 truncate">{authUser.name?.startsWith('User ') ? 'Customer' : authUser.name}</p>
            {authUser.email && <p className="text-xs text-gray-500 truncate">{authUser.email}</p>}
            {authUser.phone && <p className="text-xs text-gray-500">{authUser.phone}</p>}
          </div>
          <div className="py-1">
            {(authUser.role === 'owner' || authUser.role === 'admin' || authUser.role === 'manager') && (
              <button onClick={() => { setShowUserMenu(false); navigate('/dashboard'); }}
                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                <LayoutDashboard className="h-4 w-4 text-gray-400" /> Dashboard
              </button>
            )}
            <button onClick={() => { setShowUserMenu(false); navigate('/track-booking'); }}
              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-400" /> My Bookings
            </button>
            <button onClick={() => { setShowUserMenu(false); authLogout(); setVerifiedCustomer(null); setOtpStep('phone'); }}
              className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
              <LogIn className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </>
    )}

    {/* One-Way Trip Book Modal */}
    {onewayBookModal && bookingTrip && (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
        <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl max-h-[92vh] flex flex-col">
          {/* Header */}
          <div className={`px-6 py-5 text-white text-center ${bookSuccess ? 'bg-gradient-to-r from-green-500 to-emerald-600' : 'bg-gradient-to-r from-blue-500 to-blue-600'}`}>
            {bookSuccess ? (
              <>
                <div className="w-14 h-14 mx-auto mb-2 rounded-full bg-white/20 flex items-center justify-center">
                  <CheckCircle2 className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-bold">Booking Sent!</h3>
                <p className="text-sm text-green-100 mt-1">{titleCase(bookingTrip.fromCity)} → {titleCase(bookingTrip.toCity)}</p>
              </>
            ) : (
              <>
                <h3 className="text-lg font-bold">Book One-Way Trip</h3>
                <p className="text-sm text-blue-100 mt-1">{titleCase(bookingTrip.fromCity)} → {titleCase(bookingTrip.toCity)}</p>
              </>
            )}
          </div>

          {/* Body */}
          <div className="p-6 space-y-4 overflow-y-auto">
            {!bookSuccess ? (
              <>
                {/* Trip summary */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-500">Date</p>
                    <p className="font-semibold text-gray-900">
                      {new Date(bookingTrip.departureDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Per Seat</p>
                    <p className="font-semibold text-gray-900">₹{(bookingTrip.price || 0).toLocaleString('en-IN')}</p>
                  </div>
                  {bookingTrip.carId && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500">Vehicle</p>
                      <p className="font-semibold text-gray-900">{formatVehicleName(bookingTrip.carId)}</p>
                    </div>
                  )}
                </div>

                {/* Customer details */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Your Name *</label>
                  <input type="text" value={bookName} onChange={(e) => setBookName(e.target.value)} placeholder="Full name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Your Phone *</label>
                  <input type="tel" value={bookPhone} onChange={(e) => setBookPhone(e.target.value)} placeholder="10-digit number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Email <span className="text-gray-400">(optional)</span></label>
                  <input type="email" value={bookEmail} onChange={(e) => setBookEmail(e.target.value)} placeholder="you@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                  <p className="text-[11px] text-gray-500 mt-1">We'll email you the driver's contact after booking.</p>
                </div>

                {/* Seats + total */}
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Number of Seats (max {bookingTrip.seatsAvailable})</label>
                  <input type="number" min="1" max={bookingTrip.seatsAvailable} value={bookSeats}
                    onChange={(e) => setBookSeats(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                </div>
                <div className="bg-blue-50 rounded-lg p-3 flex items-center justify-between">
                  <span className="text-sm text-gray-700">Total</span>
                  <span className="text-lg font-bold text-blue-700">₹{((bookingTrip.price || 0) * (parseInt(bookSeats) || 0)).toLocaleString('en-IN')}</span>
                </div>
                <p className="text-[11px] text-gray-500 text-center">
                  Driver's contact stays private until your booking is sent — so they're not disturbed beforehand.
                </p>
              </>
            ) : (
              <>
                {/* Success view — reveals driver/owner contact */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-700">Your booking for <span className="font-semibold">{bookSuccess.seats} seat{bookSuccess.seats > 1 ? 's' : ''}</span> is pending owner confirmation.</p>
                  <p className="text-xs text-gray-500 mt-1">You'll receive an email once it's confirmed.</p>
                </div>

                <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
                  <div className="text-center">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Driver / Travel Owner</p>
                    <p className="text-base font-bold text-gray-900 mt-0.5">{bookSuccess.owner?.name || '—'}</p>
                  </div>
                  {bookSuccess.owner?.phone ? (
                    <div className="grid grid-cols-2 gap-2">
                      <a href={`tel:${bookSuccess.owner.phone}`}
                        className="inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg font-semibold text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700">
                        Call
                      </a>
                      <a href={`https://wa.me/${(bookSuccess.owner.phone || '').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg font-semibold text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700">
                        WhatsApp
                      </a>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-500 text-center">Phone not available; the owner will contact you directly.</p>
                  )}
                  {bookSuccess.owner?.phone && (
                    <p className="text-xs text-gray-500 text-center">{bookSuccess.owner.phone}</p>
                  )}
                </div>
              </>
            )}

            {/* Buttons */}
            {!bookSuccess ? (
              <div className="flex gap-3 pt-1">
                <button onClick={() => { setOnewayBookModal(false); setBookingTrip(null); }} disabled={bookSubmitting}
                  className="flex-1 py-2.5 rounded-xl font-semibold bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:opacity-60">
                  Cancel
                </button>
                <button onClick={submitOnewayBook} disabled={bookSubmitting}
                  className="flex-1 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:opacity-60">
                  {bookSubmitting ? 'Booking…' : 'Confirm Booking'}
                </button>
              </div>
            ) : (
              <button onClick={() => { setOnewayBookModal(false); setBookingTrip(null); setBookSuccess(null); }}
                className="w-full py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700">
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    )}

    {/* App-style feedback toast for one-way booking */}
    <ToastModal
      isOpen={bookToast.open}
      onClose={bookToast.onClose || (() => setBookToast(t => ({ ...t, open: false })))}
      title={bookToast.title}
      message={bookToast.message}
      type={bookToast.type}
    />
  </>
  );
};

// Helper function to get button background color
function getTabButtonColor(color) {
  const colors = {
    blue: '#2563eb',
    green: '#16a34a',
    purple: '#9333ea',
    orange: '#ea580c',
    teal: '#0d9488',
    indigo: '#4f46e5',
    red: '#dc2626'
  };
  return colors[color] || colors.blue;
}

// Helper function to get service badge color
function getServiceBadgeColor(type) {
  const colors = {
    'rental': 'bg-green-500',
    'daily': 'bg-purple-500',
    'km-based': 'bg-orange-500',
    'outstation': 'bg-teal-500',
    'local': 'bg-indigo-500',
    'sale': 'bg-red-500'
  };
  return colors[type] || 'bg-blue-500';
}

// Helper function to get service label
function getServiceLabel(type) {
  const labels = {
    'rental': 'Rental',
    'daily': 'Daily',
    'km-based': 'Per KM',
    'outstation': 'Outstation',
    'local': 'Local',
    'sale': 'Sale'
  };
  return labels[type] || type;
}

// Helper function to format name in Title Case
function formatName(name) {
  if (!name) return '';
  return name
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// Helper function to get sale price type label
function getSalePriceTypeLabel(type) {
  const labels = {
    'fixed': 'Fixed Price',
    'negotiable': 'Negotiable',
    'best-offer': 'Best Offer',
    'contact-for-price': 'Contact for Price'
  };
  return labels[type] || type;
}

// Helper function to get sale price type text color
function getSalePriceTypeTextColor(type) {
  const colors = {
    'fixed': 'text-green-600',
    'negotiable': 'text-orange-500',
    'best-offer': 'text-purple-600',
    'contact-for-price': 'text-blue-600'
  };
  return colors[type] || 'text-gray-500';
}

export default BrowseCars;
